import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../api/AuthContext";
import { NotificationProvider, useNotifications } from "./NotificationContext";
import NativeWebSocketService from "./nativeWebSocketService";
import NotificationBell from "./NotificationBell";
import {
  validateImageFile,
  validateVideoFile,
  uploadImageToCloudinary,
  uploadVideoToCloudinary,
  getTransformedImageUrl,
} from "../Utils/upload";
import AdminInventory from "./AdminInventory";
import "./AdminAmini.css";

function AdminAminiiContent() {
  const navigate = useNavigate();
  const { api, isAuthenticated, user, loading } = useAuth();
  const { addNotification } = useNotifications();

  const seenOrderIdsRef = useRef(new Set());
  const isInitialFetchRef = useRef(true);
  const fileInputRef = useRef(null);
  const productVideoInputRef = useRef(null);
  const desktopBannerInputRef = useRef(null);
  const mobileBannerInputRef = useRef(null);
  const reelVideoInputRef = useRef(null);

  // Active Admin Mode: "product" | "banner" | "reel"
  const [adminMode, setAdminMode] = useState("product");

  // Standard Product Form State
  const [formData, setFormData] = useState({
    productName: "",
    productPrice: "",
    productImages: [],
    productVideo: "",
    description: "",
    productCategory: {
      categoryName: "",
      subCategory: { categoryName: "" },
    },
    productDate: new Date().toLocaleDateString("en-GB").split("/").join("-"),
    productSize: [{ sizeName: "S", stock: 10 }],
    beforeDiscountPrice: "",
    status: "active",
    brand: "Aminii",
    tags: [""],
  });

  // Product video upload state
  const [productVideoUploadState, setProductVideoUploadState] = useState({
    status: "idle", // "idle" | "uploading" | "done" | "error"
    progress: 0,
    error: null,
  });

  // Per-image upload progress: { [index]: { status: 'uploading'|'done'|'error', progress: 0-100, error: string|null } }
  const [uploadStates, setUploadStates] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // Banner Form State (Safeguarded against typos — category locked to "Main Image")
  const [bannerData, setBannerData] = useState({
    headline: "",
    desktopImageUrl: "",
    mobileImageUrl: "",
    targetCategorySlug: "aminii-luxe",
  });
  const [desktopBannerUploadState, setDesktopBannerUploadState] = useState({
    status: "idle",
    progress: 0,
    error: null,
  });
  const [mobileBannerUploadState, setMobileBannerUploadState] = useState({
    status: "idle",
    progress: 0,
    error: null,
  });

  // Reel Form State
  const [reelData, setReelData] = useState({
    title: "",
    videoUrl: "",
    instagramUrl: "https://www.instagram.com/aminii.clothing.in",
    linkType: "category", // "category" or "product"
    targetValue: "aminii-luxe",
  });
  const [reelVideoUploadState, setReelVideoUploadState] = useState({
    status: "idle", // "idle" | "uploading" | "done" | "error"
    progress: 0,
    error: null,
  });
  const [isReelDragOver, setIsReelDragOver] = useState(false);
  const [showManualVideoUrl, setShowManualVideoUrl] = useState(false);

  const [existingProducts, setExistingProducts] = useState([]);
  const [editingProductId, setEditingProductId] = useState(null);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);

  // Banners Form & List State
  const [bannerFormData, setBannerFormData] = useState({
    title: "",
    imageUrl: "",
    mobileImageUrl: "",
    redirectCategory: "women/kurta-sets",
    redirectProductId: "",
    isActive: true,
  });
  const [existingBanners, setExistingBanners] = useState([]);
  const [editingBannerId, setEditingBannerId] = useState(null);

  // Reels Form & List State
  const [reelFormData, setReelFormData] = useState({
    title: "",
    videoUrl: "",
    instagramReelUrl: "",
    categorySlug: "women/kurta-sets",
    productId: "",
    displayOrder: 0,
    isActive: true,
  });
  const [existingReels, setExistingReels] = useState([]);

  const categories = [
    {
      categoryName: "Basics",
      subCategories: [
        { categoryName: "Cotton Wear" },
        { categoryName: "Daily Wear" },
        { categoryName: "Casual Sets" },
        { categoryName: "Tops & Tunics" },
      ],
    },
    {
      categoryName: "Best seller",
      subCategories: [
        { categoryName: "Trending Cordsets" },
        { categoryName: "Featured Suits" },
        { categoryName: "Festive Specials" },
      ],
    },
    {
      categoryName: "Aminii luxe",
      subCategories: [
        { categoryName: "Cordset" },
        { categoryName: "Luxe Pakistani" },
        { categoryName: "Pakistani Wear" },
        { categoryName: "Premium Suits" },
      ],
    },
    {
      categoryName: "PLUS SIZES",
      subCategories: [
        { categoryName: "Plus Size Sets" },
        { categoryName: "Plus Size Kurtas" },
        { categoryName: "Plus Size Co-ords" },
        { categoryName: "Plus Size Dresses" },
      ],
    },
  ];

  const bannerCategoryOptions = [
    { label: "Basics", value: "basics" },
    { label: "Best Seller", value: "best-seller" },
    { label: "AMINII Luxe", value: "aminii-luxe" },
    { label: "PLUS SIZES", value: "aminii-plus" },
  ];

  const categoryOptions = [
    { label: "Women - Kurta Sets", value: "women/kurta-sets" },
    { label: "Women - Dresses", value: "women/dresses" },
    { label: "Women - Co-ords", value: "women/co-ords" },
    { label: "Women - Cordset", value: "women/cordset" },
    { label: "Women - Luxe Pakistani", value: "women/luxe-pakistani" },
    { label: "Plus Sizes", value: "aminii-plus" },
    { label: "Plus", value: "plus" },
  ];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ text: "", type: "" });

  // Orders & COD Pincodes state
  const [adminOrders, setAdminOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const ORDERS_PER_PAGE = 10;
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmModalOrder, setConfirmModalOrder] = useState(null);
  const [cancelModalOrder, setCancelModalOrder] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Ready To Ship & Package Dimensions State
  const [isEditingDimensions, setIsEditingDimensions] = useState(false);
  const [shipWeight, setShipWeight] = useState("0.50");
  const [shipLength, setShipLength] = useState("10");
  const [shipWidth, setShipWidth] = useState("10");
  const [shipHeight, setShipHeight] = useState("10");
  const [shipPickupLocation, setShipPickupLocation] = useState("Aminii clothing ");

  // Product ID Search & Quick Inspection State
  const [orderSearchQuery, setOrderSearchQuery] = useState("");
  const [productLookupId, setProductLookupId] = useState("");
  const [inspectedProduct, setInspectedProduct] = useState(null);
  const [loadingProductLookup, setLoadingProductLookup] = useState(false);
  const [productLookupError, setProductLookupError] = useState("");

  const handleLookupProduct = async (idToLookup, sizeContext = null) => {
    const id = idToLookup !== undefined ? idToLookup : productLookupId;
    if (!id || !String(id).trim()) return;
    setLoadingProductLookup(true);
    setProductLookupError("");
    try {
      const res = await api.get(`/products/${String(id).trim()}`);
      if (res.data?.data) {
        setInspectedProduct({ ...res.data.data, orderedSizeContext: sizeContext });
      } else {
        setProductLookupError(`Product #${id} not found.`);
      }
    } catch (err) {
      setProductLookupError(err.response?.data?.message || `Product #${id} not found.`);
    } finally {
      setLoadingProductLookup(false);
    }
  };

  const filteredOrders = useMemo(() => {
    if (!orderSearchQuery.trim()) return adminOrders;
    const q = orderSearchQuery.trim().toLowerCase();
    return adminOrders.filter((ord) => {
      const matchOrderId = String(ord.orderId).toLowerCase().includes(q);
      const matchCustomer = ord.customerName?.toLowerCase().includes(q);
      const matchPhone = ord.customerPhone?.toLowerCase().includes(q);
      const matchEmail = ord.customerEmail?.toLowerCase().includes(q);
      const matchAwb = ord.shiprocket?.awbCode?.toLowerCase().includes(q);
      const matchProduct = ord.orderedProducts?.some((p) =>
        String(p.productId).toLowerCase().includes(q) ||
        p.productName?.toLowerCase().includes(q) ||
        p.sku?.toLowerCase().includes(q) ||
        p.size?.toLowerCase().includes(q)
      );
      return matchOrderId || matchCustomer || matchPhone || matchEmail || matchAwb || matchProduct;
    });
  }, [adminOrders, orderSearchQuery]);

  const [codPincodesList, setCodPincodesList] = useState([]);
  const [newPincode, setNewPincode] = useState("");
  const [newAreaName, setNewAreaName] = useState("");
  const [loadingPincodes, setLoadingPincodes] = useState(false);

  const fetchAdminOrders = async (silent = false) => {
    if (!silent) setLoadingOrders(true);
    try {
      const res = await api.get("/orders");
      const list = res.data?.data || res.data || [];
      const orderArray = Array.isArray(list) ? list : [];

      // Sort descending by orderId so NEWEST orders (e.g. #13) appear at top of Page 1
      orderArray.sort((a, b) => Number(b.orderId) - Number(a.orderId));

      setAdminOrders(orderArray);

      // Check for new orders to trigger live notification + sound chime
      orderArray.forEach((ord) => {
        const orderIdStr = String(ord.orderId);
        if (!seenOrderIdsRef.current.has(orderIdStr)) {
          if (!isInitialFetchRef.current) {
            NativeWebSocketService.playNotificationSound();
            NativeWebSocketService.showBrowserNotification(
              " New Order Received!",
              `Order #${ord.orderId} from ${ord.customerName || "Customer"} - ₹${ord.totalPrice}`
            );

            addNotification({
              id: `ord_${ord.orderId}_${Date.now()}`,
              type: "new_order",
              title: " New Order Received",
              message: `Order #${ord.orderId} placed by ${ord.customerName || "Customer"}`,
              amount: ord.totalPrice,
              orderId: ord.orderId,
              timestamp: ord.orderDate || new Date().toLocaleTimeString(),
              data: ord,
            });
          }
          seenOrderIdsRef.current.add(orderIdStr);
        }
      });

      isInitialFetchRef.current = false;
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    } finally {
      if (!silent) setLoadingOrders(false);
    }
  };

  const fetchCodPincodes = async () => {
    setLoadingPincodes(true);
    try {
      const res = await api.get("/admin/cod-pincodes");
      const list = res.data?.data || res.data || [];
      setCodPincodesList(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to fetch COD pincodes:", err);
    } finally {
      setLoadingPincodes(false);
    }
  };

  useEffect(() => {
    if (adminMode === "orders") {
      fetchAdminOrders(false);
      const interval = setInterval(() => {
        fetchAdminOrders(true);
      }, 10000);
      return () => clearInterval(interval);
    } else if (adminMode === "codPincodes") {
      fetchCodPincodes();
    }
  }, [adminMode]);

  useEffect(() => {
    const handleSelectAdminOrder = (e) => {
      const targetId = e.detail?.orderId;
      if (targetId) {
        setAdminMode("orders");
        const found = adminOrders.find((o) => String(o.orderId) === String(targetId));
        if (found) {
          setSelectedOrder(found);
        }
      }
    };
    window.addEventListener("selectAdminOrder", handleSelectAdminOrder);
    return () => window.removeEventListener("selectAdminOrder", handleSelectAdminOrder);
  }, [adminOrders]);

  const handleExecuteConfirmOrder = async () => {
    if (!confirmModalOrder) return;
    setActionLoading(true);
    try {
      await api.post(`/orders/${confirmModalOrder.orderId}/confirm`);
      setSubmitMessage({ type: "success", text: `Order #${confirmModalOrder.orderId} confirmed successfully!` });
      setConfirmModalOrder(null);
      fetchAdminOrders();
      if (selectedOrder && selectedOrder.orderId === confirmModalOrder.orderId) {
        setSelectedOrder((prev) => ({ ...prev, orderStatus: "ORDER_CONFIRMED" }));
      }
    } catch (err) {
      setSubmitMessage({ type: "error", text: err.response?.data?.message || "Failed to confirm order" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenOrderDetails = (ord) => {
    if (!ord) {
      setSelectedOrder(null);
      return;
    }
    let calculatedWeight = ord.packageWeight;
    if ((!calculatedWeight || calculatedWeight <= 0) && ord.orderedProducts && ord.orderedProducts.length > 0) {
      calculatedWeight = ord.orderedProducts.reduce((sum, item) => {
        const itemW = item.weight && Number(item.weight) > 0 ? Number(item.weight) : 0.5;
        return sum + (itemW * (item.quantity || 1));
      }, 0);
    }
    if (!calculatedWeight || calculatedWeight <= 0) {
      calculatedWeight = 0.5;
    }
    setShipWeight(String(Number(calculatedWeight).toFixed(2)));
    setShipLength(String(ord.packageLength || 10));
    setShipWidth(String(ord.packageWidth || 10));
    setShipHeight(String(ord.packageHeight || 10));
    setShipPickupLocation(ord.pickupLocation && ord.pickupLocation !== "Primary" ? ord.pickupLocation : "Aminii clothing ");
    setSelectedOrder(ord);
  };

  const handleExecuteReadyToShip = async () => {
    if (!selectedOrder) return;
    setActionLoading(true);
    try {
      const payload = {
        weight: parseFloat(shipWeight) || 0.5,
        length: parseFloat(shipLength) || 10,
        width: parseFloat(shipWidth) || 10,
        height: parseFloat(shipHeight) || 10,
        pickupLocation: (shipPickupLocation?.trim() && shipPickupLocation.trim() !== "Primary") ? shipPickupLocation.trim() : "Aminii clothing "
      };
      const res = await api.post(`/orders/${selectedOrder.orderId}/ready-to-ship`, payload);
      setSubmitMessage({ type: "success", text: `Order #${selectedOrder.orderId} marked as Ready to Ship!` });
      fetchAdminOrders();
      if (res.data?.data) {
        setSelectedOrder(res.data.data);
      }
    } catch (err) {
      setSubmitMessage({ type: "error", text: err.response?.data?.message || "Failed to process Shiprocket shipping" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleExecuteCancelOrder = async () => {
    if (!cancelModalOrder) return;
    setActionLoading(true);
    try {
      await api.post(`/orders/${cancelModalOrder.orderId}/cancel`, { reason: cancelReason || "Cancelled by admin" });
      setSubmitMessage({ type: "success", text: `Order #${cancelModalOrder.orderId} cancelled successfully!` });
      setCancelModalOrder(null);
      setCancelReason("");
      fetchAdminOrders();
    } catch (err) {
      setSubmitMessage({ type: "error", text: err.response?.data?.message || "Failed to cancel order" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadLabel = async (orderId, existingLabelUrl) => {
    if (existingLabelUrl) {
      window.open(existingLabelUrl, "_blank");
      return;
    }
    setActionLoading(true);
    try {
      const res = await api.get(`/admin/orders/${orderId}/label`);
      const labelUrl = res.data?.data?.labelUrl;
      if (labelUrl) {
        window.open(labelUrl, "_blank");
      } else {
        setSubmitMessage({ type: "error", text: "Label URL not available" });
      }
    } catch (err) {
      setSubmitMessage({ type: "error", text: err.response?.data?.message || "Failed to fetch shipping label" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadManifest = async (orderId, existingManifestUrl) => {
    if (existingManifestUrl) {
      window.open(existingManifestUrl, "_blank");
      return;
    }
    setActionLoading(true);
    try {
      const res = await api.get(`/admin/orders/${orderId}/manifest`);
      const manifestUrl = res.data?.data?.manifestUrl;
      if (manifestUrl) {
        window.open(manifestUrl, "_blank");
      } else {
        setSubmitMessage({ type: "error", text: "Manifest URL not available" });
      }
    } catch (err) {
      setSubmitMessage({ type: "error", text: err.response?.data?.message || "Failed to fetch shipping manifest" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddCodPincode = async (e) => {
    e.preventDefault();
    if (!newPincode.trim()) return;
    try {
      await api.post("/admin/cod-pincodes", { pincode: newPincode.trim(), areaName: newAreaName.trim() });
      setNewPincode("");
      setNewAreaName("");
      setSubmitMessage({ type: "success", text: "COD Pincode added successfully!" });
      fetchCodPincodes();
    } catch (err) {
      setSubmitMessage({ type: "error", text: err.response?.data?.message || "Failed to add pincode" });
    }
  };

  const handleDeleteCodPincode = async (pincode) => {
    try {
      await api.delete(`/admin/cod-pincodes/${pincode}`);
      setSubmitMessage({ type: "success", text: `Pincode ${pincode} removed successfully!` });
      fetchCodPincodes();
    } catch (err) {
      setSubmitMessage({ type: "error", text: "Failed to delete pincode" });
    }
  };

  // Fetch Existing Reels and Banners for management
  const fetchExistingContent = async () => {
    try {
      // Fetch Reels
      const reelRes = await api.get("/reels");
      const reelList = reelRes.data?.data || reelRes.data || [];
      setExistingReels(Array.isArray(reelList) ? reelList : []);

      // Fetch Banners (filtered by Main Image category)
      const prodRes = await api.get("/products");
      const allProds = prodRes.data?.data || [];
      const banners = allProds.filter((p) =>
        p.productCategory?.some(
          (cat) =>
            cat.categoryName?.toLowerCase().replace(/[-_\s]+/g, "") === "mainimage"
        )
      );
      setExistingBanners(banners);
    } catch (err) {
      console.error("Error fetching existing admin content:", err);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchExistingContent();
    }
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="admin-aminii">
        <div className="admin-page">
          <div className="admin-loading-center">
            <div className="admin-spinner"></div>
            <h2>Loading Aminii Admin Control...</h2>
          </div>
        </div>
      </div>
    );
  }

  // Direct JWT Payload Decoding Fallback
  const getStoredToken = () =>
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    null;

  const decodeJwt = (token) => {
    try {
      if (!token) return null;
      const base64Url = token.split(".")[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  };

  const activeToken = getStoredToken();
  const jwtClaims = decodeJwt(activeToken);

  const rawRole =
    user?.userRole ||
    user?.role ||
    jwtClaims?.userRole ||
    jwtClaims?.role ||
    localStorage.getItem("userRole") ||
    "";

  const isAdminUser = rawRole.toString().toUpperCase().includes("ADMIN");
  const isAuthorized = (isAuthenticated || !!activeToken) && isAdminUser;

  if (!isAuthorized) {
    return (
      <div className="admin-aminii">
        <div className="admin-page">
          <div className="admin-access-denied-container">
            <div className="admin-access-denied-card">
              <span className="admin-access-brand">A M I N I I</span>
              <h2 className="admin-access-title">ACCESS DENIED</h2>
              <p className="admin-access-msg">
                You must be logged in with an Administrative account (ADMIN) to access the Aminii Control Panel.
              </p>
              <div className="admin-access-actions">
                <button
                  onClick={() => navigate("/login", { state: { from: "/AdminAminii" } })}
                  className="admin-access-btn gold"
                >
                  Login as Admin
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="admin-access-btn outline"
                >
                  Return to Store
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Handlers for Standard Products ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCategoryChange = (e) => {
    const categoryName = e.target.value;
    setFormData({
      ...formData,
      productCategory: {
        categoryName,
        subCategory: { categoryName: "" },
      },
    });
  };

  const handleSubCategoryChange = (e) => {
    const subCategoryName = e.target.value;
    setFormData({
      ...formData,
      productCategory: {
        ...formData.productCategory,
        subCategory: { categoryName: subCategoryName },
      },
    });
  };

  // ─── Cloudinary Image Upload Handlers ────────────────────────────────────

  const processFiles = async (files) => {
    const validFiles = [];
    const errors = [];
    for (const file of files) {
      const { valid, error } = validateImageFile(file);
      if (valid) {
        validFiles.push(file);
      } else {
        errors.push(error);
      }
    }

    if (errors.length > 0) {
      alert(errors.join("\n"));
    }

    const startIndex = formData.productImages.length;
    const totalAfter = startIndex + validFiles.length;
    if (totalAfter > 10) {
      alert(`You can have a maximum of 10 images. Currently have ${startIndex}, tried to add ${validFiles.length}.`);
      return;
    }

    if (validFiles.length === 0) return;

    // Reserve slots with empty string while uploading
    const placeholders = validFiles.map(() => "");
    setFormData((prev) => ({ ...prev, productImages: [...prev.productImages, ...placeholders] }));
    setUploadStates((prev) => [
      ...prev,
      ...validFiles.map(() => ({ status: "uploading", progress: 0, error: null })),
    ]);

    // Upload each file in parallel
    validFiles.forEach(async (file, i) => {
      const imgIndex = startIndex + i;
      try {
        const url = await uploadImageToCloudinary(file, (percent) => {
          setUploadStates((prev) => {
            const next = [...prev];
            next[imgIndex] = { ...next[imgIndex], progress: percent };
            return next;
          });
        });
        setFormData((prev) => {
          const imgs = [...prev.productImages];
          imgs[imgIndex] = url;
          return { ...prev, productImages: imgs };
        });
        setUploadStates((prev) => {
          const next = [...prev];
          next[imgIndex] = { status: "done", progress: 100, error: null };
          return next;
        });
      } catch (err) {
        setUploadStates((prev) => {
          const next = [...prev];
          next[imgIndex] = { status: "error", progress: 0, error: err.message };
          return next;
        });
      }
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) processFiles(files);
    // Reset so same file can be picked again if needed
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    processFiles(files);
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => {
      const imgs = prev.productImages.filter((_, i) => i !== index);
      return { ...prev, productImages: imgs };
    });
    setUploadStates((prev) => prev.filter((_, i) => i !== index));
    if (previewImageIndex >= index && previewImageIndex > 0) {
      setPreviewImageIndex((p) => p - 1);
    }
  };

  // ─── Banner Image Upload Handler (Desktop & Mobile) ───────────────────────
  const handleBannerFileUpload = async (file, type) => {
    if (!file) return;
    const { valid, error } = validateImageFile(file);
    if (!valid) {
      alert(error);
      return;
    }

    const setUploadState =
      type === "desktop"
        ? setDesktopBannerUploadState
        : setMobileBannerUploadState;

    setUploadState({ status: "uploading", progress: 0, error: null });

    try {
      const rawUrl = await uploadImageToCloudinary(file, (percent) => {
        setUploadState((prev) => ({ ...prev, progress: percent }));
      });

      // Apply tailored responsive dimensions to Cloudinary URL
      const transformedUrl =
        type === "desktop"
          ? getTransformedImageUrl(rawUrl, { width: 1920, height: 700, crop: "fill" })
          : getTransformedImageUrl(rawUrl, { width: 800, height: 1100, crop: "fill" });

      if (type === "desktop") {
        setBannerData((prev) => ({ ...prev, desktopImageUrl: transformedUrl }));
      } else {
        setBannerData((prev) => ({ ...prev, mobileImageUrl: transformedUrl }));
      }

      setUploadState({ status: "done", progress: 100, error: null });
    } catch (err) {
      setUploadState({ status: "error", progress: 0, error: err.message });
    }
  };

  // ─── Reel Video Upload Handler (Cloudinary Direct) ────────────────────────
  const handleReelVideoUpload = async (file) => {
    if (!file) return;
    const { valid, error } = validateVideoFile(file);
    if (!valid) {
      alert(error);
      return;
    }

    setReelVideoUploadState({ status: "uploading", progress: 0, error: null });

    try {
      const secureUrl = await uploadVideoToCloudinary(file, (percent) => {
        setReelVideoUploadState((prev) => ({ ...prev, progress: percent }));
      });

      setReelData((prev) => ({ ...prev, videoUrl: secureUrl }));
      setReelVideoUploadState({ status: "done", progress: 100, error: null });
    } catch (err) {
      setReelVideoUploadState({
        status: "error",
        progress: 0,
        error: err.message || "Failed to upload video",
      });
      alert(`Video upload failed: ${err.message || "Unknown error"}`);
    }
  };

  // ─── Product Video Upload Handler (Cloudinary Direct) ───────────────────────
  const handleProductVideoUpload = async (file) => {
    if (!file) return;
    const { valid, error } = validateVideoFile(file);
    if (!valid) {
      alert(error);
      return;
    }

    setProductVideoUploadState({ status: "uploading", progress: 0, error: null });

    try {
      const secureUrl = await uploadVideoToCloudinary(file, (percent) => {
        setProductVideoUploadState((prev) => ({ ...prev, progress: percent }));
      });

      setFormData((prev) => ({ ...prev, productVideo: secureUrl }));
      setProductVideoUploadState({ status: "done", progress: 100, error: null });
    } catch (err) {
      setProductVideoUploadState({
        status: "error",
        progress: 0,
        error: err.message || "Failed to upload video",
      });
      alert(`Product video upload failed: ${err.message || "Unknown error"}`);
    }
  };

  // ─── End Media Upload Handlers ────────────────────────────────────────────

  const handleSizeChange = (index, field, value) => {
    const updatedSizes = [...formData.productSize];
    updatedSizes[index] = {
      ...updatedSizes[index],
      [field]: field === "stock" ? parseInt(value, 10) || 0 : value,
    };
    setFormData({ ...formData, productSize: updatedSizes });
  };

  const handleAddSize = () => {
    const STANDARD_SIZES = ["S", "M", "L", "XL", "XXL", "3XL", "4XL", "5XL"];
    const existingSizeNames = formData.productSize.map((s) => (s.sizeName || "").toUpperCase().trim());

    // Find the next standard size that hasn't been added yet
    const nextSize = STANDARD_SIZES.find((sz) => !existingSizeNames.includes(sz)) || "";

    setFormData({
      ...formData,
      productSize: [...formData.productSize, { sizeName: nextSize, stock: 10 }],
    });
  };

  const handleRemoveSize = (index) => {
    if (formData.productSize.length > 1) {
      const updatedSizes = formData.productSize.filter((_, i) => i !== index);
      setFormData({ ...formData, productSize: updatedSizes });
    }
  };

  // --- Submit Standard Product ---
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ text: "", type: "" });

    // 1. Check if any image is currently uploading
    const isStillUploading = uploadStates.some((s) => s.status === "uploading");
    if (isStillUploading) {
      setIsSubmitting(false);
      const msg = "Please wait for all images to finish uploading before publishing.";
      setSubmitMessage({ text: msg, type: "error" });
      alert(msg);
      return;
    }

    // 2. Validate price logic
    const productPrice = parseFloat(formData.productPrice);
    const beforeDiscountPrice = formData.beforeDiscountPrice
      ? parseFloat(formData.beforeDiscountPrice)
      : null;

    if (beforeDiscountPrice !== null && beforeDiscountPrice <= productPrice) {
      setIsSubmitting(false);
      const msg = "Original Price (₹" + beforeDiscountPrice + ") must be greater than Sale Price (₹" + productPrice + "), or leave it blank.";
      setSubmitMessage({
        text: msg,
        type: "error",
      });
      alert(msg);
      return;
    }

    const validImages = formData.productImages.filter((img) => img && img.trim() !== "");
    if (validImages.length === 0) {
      setIsSubmitting(false);
      const msg = "Please upload at least 1 product image before publishing.";
      setSubmitMessage({ text: msg, type: "error" });
      alert(msg);
      return;
    }

    const productData = {
      ...formData,
      productPrice,
      beforeDiscountPrice,
      productImages: validImages,
      productVideo: formData.productVideo || undefined,
      tags: formData.tags.filter((tag) => tag.trim() !== ""),
      productCategory: [formData.productCategory],
    };

    try {
      const response = await api.post("/all-product-curd", productData);
      if (response.data.success) {
        setSubmitMessage({
          text: "✨ Product added to store inventory!",
          type: "success",
        });
        setFormData({
          productName: "",
          productPrice: "",
          productImages: [],
          productVideo: "",
          description: "",
          productCategory: {
            categoryName: "",
            subCategory: { categoryName: "" },
          },
          productDate: new Date().toLocaleDateString("en-GB").split("/").join("-"),
          productSize: [{ sizeName: "S", stock: 10 }],
          beforeDiscountPrice: "",
          status: "active",
          brand: "Aminii",
          tags: [""],
        });
        setUploadStates([]);
        setProductVideoUploadState({ status: "idle", progress: 0, error: null });
        setPreviewImageIndex(0);
      } else {
        throw new Error(response.data.message || "Failed to add product");
      }
    } catch (error) {
      setSubmitMessage({
        text: `Error: ${error.response?.data?.message || error.message}`,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Submit Hero Banner (SAFEGUARDED: categoryName is locked to "Main Image") ---
  const handleBannerSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ text: "", type: "" });

    if (!bannerData.desktopImageUrl.trim()) {
      setIsSubmitting(false);
      setSubmitMessage({ text: "Desktop image URL is required.", type: "error" });
      return;
    }

    const desktopImg = bannerData.desktopImageUrl.trim();
    const mobileImg = bannerData.mobileImageUrl.trim() || desktopImg;

    // Hardcode categoryName to "Main Image" — safe from any user typos!
    const bannerPayload = {
      productName: bannerData.headline.trim() || "Main Image",
      productPrice: 0,
      productImages: [desktopImg, mobileImg],
      description: "Homepage Luxury Banner",
      productCategory: [
        {
          categoryName: "Main Image",
          subCategory: { categoryName: bannerData.targetCategorySlug },
        },
      ],
      productDate: new Date().toLocaleDateString("en-GB").split("/").join("-"),
      productSize: [{ sizeName: "STANDARD", stock: 999 }],
      status: "active",
      brand: "Aminii",
      tags: ["banner"],
    };

    try {
      const response = await api.post("/all-product-curd", bannerPayload);
      if (response.data.success) {
        setSubmitMessage({
          text: "🖼️ Hero Banner created & published to homepage!",
          type: "success",
        });
        setBannerData({
          headline: "",
          desktopImageUrl: "",
          mobileImageUrl: "",
          targetCategorySlug: "aminii-luxe",
        });
        setDesktopBannerUploadState({ status: "idle", progress: 0, error: null });
        setMobileBannerUploadState({ status: "idle", progress: 0, error: null });
        fetchExistingContent();
      } else {
        throw new Error(response.data.message || "Failed to create banner");
      }
    } catch (error) {
      setSubmitMessage({
        text: `Error creating banner: ${error.response?.data?.message || error.message}`,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Submit Reel ---
  const handleReelSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ text: "", type: "" });

    if (reelVideoUploadState.status === "uploading") {
      setIsSubmitting(false);
      setSubmitMessage({
        text: "Please wait for the video upload to complete before publishing.",
        type: "error",
      });
      return;
    }

    if (!reelData.videoUrl.trim()) {
      setIsSubmitting(false);
      setSubmitMessage({
        text: "Please upload a video file (.mp4, .mov) or enter a video URL.",
        type: "error",
      });
      return;
    }

    const reelPayload = {
      title: reelData.title.trim() || "Aminii Lookbook Reel",
      videoUrl: reelData.videoUrl.trim(),
      instagramUrl:
        reelData.instagramUrl.trim() ||
        "https://www.instagram.com/aminii.clothing.in",
      productId:
        reelData.linkType === "product"
          ? parseInt(reelData.targetValue, 10) || null
          : null,
      categorySlug:
        reelData.linkType === "category"
          ? reelData.targetValue.trim() || "aminii-luxe"
          : null,
    };

    try {
      const response = await api.post("/reels", reelPayload);
      if (response.data.success) {
        setSubmitMessage({
          text: "🎬 Reel published successfully!",
          type: "success",
        });
        setReelData({
          title: "",
          videoUrl: "",
          instagramUrl: "https://www.instagram.com/aminii.clothing.in",
          linkType: "category",
          targetValue: "aminii-luxe",
        });
        setReelVideoUploadState({ status: "idle", progress: 0, error: null });
        fetchExistingContent();
      } else {
        throw new Error(response.data.message || "Failed to create reel");
      }
    } catch (error) {
      setSubmitMessage({
        text: `Error publishing reel: ${error.response?.data?.message || error.message}`,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Delete Reel ---
  const handleDeleteReel = async (id) => {
    if (!window.confirm("Are you sure you want to delete this reel?")) return;
    try {
      await api.delete(`/reels/${id}`);
      fetchExistingContent();
    } catch (error) {
      alert("Failed to delete reel.");
    }
  };

  // --- Delete Banner ---
  const handleDeleteBanner = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;
    try {
      await api.delete(`/all-product-curd/${productId}`);
      fetchExistingContent();
    } catch (error) {
      alert("Failed to delete banner.");
    }
  };

  return (
      <div className="admin-aminii">
        <div className="admin-page">
          {/* Top Luxury Navbar */}
          <header className="admin-navbar">
            <div className="admin-brand" onClick={() => navigate("/")}>
              <h1 className="admin-brand-main">A M I N I I</h1>
              <span className="admin-brand-sub">LUXURY CONTROL</span>
            </div>

            <div className="admin-navbar-actions">
              <span className="admin-user-badge">
                👤 {user?.username || "Aminii Admin"}
              </span>
              <NotificationBell />
              <button
                className="admin-nav-link-btn"
                onClick={() => navigate("/admin/bulk-upload")}
              >
                Bulk Upload
              </button>
              <button
                className="admin-logout-btn"
                onClick={() => {
                  localStorage.clear();
                  navigate("/login");
                }}
              >
                Logout
              </button>
            </div>
          </header>

          <main className="admin-container">
            {/* Top Mode Selector Tabs */}
            <div className="admin-mode-selector">
              <button
                className={`admin-mode-tab ${adminMode === "product" ? "active" : ""}`}
                onClick={() => setAdminMode("product")}
              >
                Manage Products
              </button>
              <button
                className={`admin-mode-tab ${adminMode === "banner" ? "active" : ""}`}
                onClick={() => setAdminMode("banner")}
              >
               Homepage Banners
              </button>
              <button
                className={`admin-mode-tab ${adminMode === "reel" ? "active" : ""}`}
                onClick={() => setAdminMode("reel")}
              >
               Reels & Lookbook
              </button>
              <button
                className={`admin-mode-tab ${adminMode === "orders" ? "active" : ""}`}
                onClick={() => setAdminMode("orders")}
              >
               Manage Orders
              </button>
              <button
                className={`admin-mode-tab ${adminMode === "inventory" ? "active" : ""}`}
                onClick={() => setAdminMode("inventory")}
              >
                Inventory
              </button>
            </div>

            {/* Submit Toast Message */}
            {submitMessage.text && (
              <motion.div
                className={`admin-toast ${submitMessage.type}`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {submitMessage.text}
              </motion.div>
            )}

            {/* MODE 1: STANDARD PRODUCTS */}
            {adminMode === "product" && (
              <div className="admin-content-grid">
                <form className="admin-form-card" onSubmit={handleProductSubmit}>
                  <div className="admin-card-header">
                    <h2>Add Clothing Product</h2>
                    <p>Add a new luxury item to your store catalogue</p>
                  </div>

                  <div className="admin-form-group">
                    <label>Product Title *</label>
                    <input
                      type="text"
                      name="productName"
                      value={formData.productName}
                      onChange={handleInputChange}
                      placeholder="e.g. PAKISTANI EMBROIDERED CORDSET"
                      required
                      className="admin-input"
                    />
                  </div>

                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Sale Price (₹) *</label>
                      <input
                        type="number"
                        name="productPrice"
                        value={formData.productPrice}
                        onChange={handleInputChange}
                        placeholder="4300"
                        required
                        className="admin-input"
                        step="0.01"
                      />
                    </div>
                    <div className="admin-form-group">
                      <label>Original Price (₹) (Optional)</label>
                      <input
                        type="number"
                        name="beforeDiscountPrice"
                        value={formData.beforeDiscountPrice}
                        onChange={handleInputChange}
                        placeholder="5000"
                        className="admin-input"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Category *</label>
                      <select
                        value={formData.productCategory.categoryName}
                        onChange={handleCategoryChange}
                        required
                        className="admin-input"
                      >
                        <option value="">Select Main Category</option>
                        {categories.map((c, i) => (
                          <option key={i} value={c.categoryName}>
                            {c.categoryName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {formData.productCategory.categoryName && (
                      <div className="admin-form-group">
                        <label>Subcategory (Optional - Select or Type)</label>
                        <input
                          type="text"
                          list="admin-subcat-options"
                          value={
                            formData.productCategory.subCategory?.categoryName || ""
                          }
                          onChange={handleSubCategoryChange}
                          placeholder="Select or type custom subcategory..."
                          className="admin-input"
                        />
                        <datalist id="admin-subcat-options">
                          {categories
                            .find(
                              (c) =>
                                c.categoryName ===
                                formData.productCategory.categoryName
                            )
                            ?.subCategories.map((sc, i) => (
                              <option key={i} value={sc.categoryName} />
                            ))}
                        </datalist>
                      </div>
                    )}
                  </div>

                  <div className="admin-form-group">
                    <label>Description *</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Enter detailed product description, fabric info, etc."
                      required
                      className="admin-input"
                      rows={3}
                    />
                  </div>

                  {/* Cloudinary Image Upload */}
                  <div className="admin-section-box">
                    <label className="admin-box-title">
                      Product Images
                      <span className="admin-box-subtitle">
                        {" "}Upload up to 10 images. Files are compressed & stored on Cloudflare R2.
                      </span>
                    </label>

                    {/* Dropzone */}
                    <div
                      className={`admin-upload-dropzone ${isDragOver ? "drag-over" : ""} ${formData.productImages.length >= 10 ? "disabled" : ""}`}
                      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                      onDragLeave={() => setIsDragOver(false)}
                      onDrop={handleDrop}
                      onClick={() => formData.productImages.length < 10 && fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleFileSelect}
                      />
                      <div className="admin-upload-dropzone-inner">
                        <span className="admin-upload-icon">📸</span>
                        {formData.productImages.length >= 10 ? (
                          <p>Maximum 10 images reached.</p>
                        ) : (
                          <>
                            <p><strong>Click to browse</strong> or drag &amp; drop images here</p>
                            <p className="admin-upload-hint">JPG, PNG, WebP — max 10MB each — up to 10 images</p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Uploaded image previews with progress */}
                    {formData.productImages.length > 0 && (
                      <div className="admin-upload-preview-grid">
                        {formData.productImages.map((url, index) => {
                          const state = uploadStates[index] || { status: "done", progress: 100 };
                          return (
                            <div key={index} className={`admin-upload-thumb ${state.status === "error" ? "thumb-error" : ""}`}>
                              {/* Thumbnail */}
                              <div className="admin-upload-thumb-img-wrap">
                                {url ? (
                                  <img
                                    src={url}
                                    alt={`Upload ${index + 1}`}
                                    className="admin-upload-thumb-img"
                                  />
                                ) : (
                                  <div className="admin-upload-thumb-placeholder">
                                    {state.status === "error" ? "⚠️" : "⏳"}
                                  </div>
                                )}
                                {/* Progress overlay while uploading */}
                                {state.status === "uploading" && (
                                  <div className="admin-upload-progress-overlay">
                                    <div
                                      className="admin-upload-progress-bar"
                                      style={{ width: `${state.progress}%` }}
                                    />
                                    <span className="admin-upload-progress-label">{state.progress}%</span>
                                  </div>
                                )}
                                {/* Status badges */}
                                {state.status === "done" && url && (
                                  <span className="admin-thumb-badge badge-done">✓</span>
                                )}
                                {state.status === "error" && (
                                  <span className="admin-thumb-badge badge-error" title={state.error}>✕</span>
                                )}
                              </div>
                              {/* Remove button */}
                              <button
                                type="button"
                                className="admin-upload-thumb-remove"
                                onClick={() => handleRemoveImage(index)}
                                title="Remove image"
                              >
                                ✕
                              </button>
                              <span className="admin-upload-thumb-num">{index + 1}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Error messages */}
                    {uploadStates.some((s) => s.status === "error") && (
                      <div className="admin-upload-errors">
                        {uploadStates.map((s, i) =>
                          s.status === "error" ? (
                            <p key={i} className="admin-upload-error-line">Image {i + 1}: {s.error}</p>
                          ) : null
                        )}
                      </div>
                    )}
                  </div>
                  {/* ─── Product Video Upload ─────────────────────────────────────── */}
                  <div className="admin-section-box">
                    <label className="admin-box-title">
                      Product Video
                      <span className="admin-box-subtitle">
                        {" "}Optional · Will appear as the 2nd slide in the product gallery. MP4, MOV, WebM — max 200MB.
                      </span>
                    </label>

                    {/* Hidden Video Input */}
                    <input
                      ref={productVideoInputRef}
                      type="file"
                      accept="video/mp4,video/mov,video/quicktime,video/webm"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleProductVideoUpload(file);
                        e.target.value = "";
                      }}
                    />

                    {formData.productVideo ? (
                      /* Video Preview Card with Player */
                      <div className="admin-video-preview-card">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                          <span style={{ fontSize: "12px", fontWeight: 600, color: "#c4b5fd", display: "flex", alignItems: "center", gap: "6px" }}>
                            🎬 Video Preview
                          </span>
                          <span style={{ fontSize: "11px", color: "#a1a1aa" }}>
                            {productVideoUploadState.status === "uploading" ? `Uploading ${productVideoUploadState.progress}%` : "Ready"}
                          </span>
                        </div>

                        {productVideoUploadState.status === "uploading" && (
                          <div className="admin-video-progress-bar-wrap" style={{ marginBottom: "10px" }}>
                            <div
                              className="admin-video-progress-bar"
                              style={{ width: `${productVideoUploadState.progress}%` }}
                            />
                          </div>
                        )}

                        <video
                          src={formData.productVideo}
                          controls
                          playsInline
                          preload="metadata"
                          className="admin-video-player"
                        />

                        <p className="admin-video-url-preview" style={{ marginTop: "6px", marginBottom: "10px" }}>
                          {formData.productVideo}
                        </p>

                        <div style={{ display: "flex", gap: "10px" }}>
                          <button
                            type="button"
                            className="admin-add-sub-btn"
                            style={{ flex: 1, margin: 0, padding: "8px 12px", fontSize: "12px" }}
                            disabled={productVideoUploadState.status === "uploading"}
                            onClick={() => productVideoInputRef.current?.click()}
                          >
                            {productVideoUploadState.status === "uploading" ? "⏳ Uploading..." : "🔄 Replace Video"}
                          </button>
                          <button
                            type="button"
                            className="admin-remove-btn"
                            style={{ margin: 0, padding: "8px 12px", fontSize: "12px" }}
                            onClick={() => {
                              setFormData((prev) => ({ ...prev, productVideo: "" }));
                              setProductVideoUploadState({ status: "idle", progress: 0, error: null });
                            }}
                          >
                            ✕ Remove Video
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Video Dropzone */
                      <div
                        className={`admin-upload-dropzone admin-video-dropzone ${productVideoUploadState.status === "uploading" ? "disabled" : ""}`}
                        onClick={() => productVideoUploadState.status !== "uploading" && productVideoInputRef.current?.click()}
                      >
                        <div className="admin-upload-dropzone-inner">
                          {productVideoUploadState.status === "uploading" ? (
                            <>
                              <span className="admin-upload-icon">⏳</span>
                              <p><strong>Uploading video…</strong> {productVideoUploadState.progress}%</p>
                              <div className="admin-video-progress-bar-wrap">
                                <div
                                  className="admin-video-progress-bar"
                                  style={{ width: `${productVideoUploadState.progress}%` }}
                                />
                              </div>
                            </>
                          ) : productVideoUploadState.status === "error" ? (
                            <>
                              <span className="admin-upload-icon">❌</span>
                              <p><strong>Upload failed.</strong> {productVideoUploadState.error} — click to retry.</p>
                            </>
                          ) : (
                            <>
                              <span className="admin-upload-icon">🎬</span>
                              <p><strong>Click to browse</strong> a product video</p>
                              <p className="admin-upload-hint">MP4, MOV, WebM — max 200 MB (appears as 2nd slide)</p>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>


                  {/* Sizes */}
                  <div className="admin-section-box">
                    <label className="admin-box-title">Sizes & Inventory</label>
                    {formData.productSize.map((sz, index) => (
                      <div key={index} className="admin-size-row">
                        <input
                          type="text"
                          value={sz.sizeName}
                          onChange={(e) =>
                            handleSizeChange(index, "sizeName", e.target.value)
                          }
                          placeholder="Size (S, M, L)"
                          className="admin-input"
                          required
                        />
                        <input
                          type="number"
                          value={sz.stock}
                          onChange={(e) =>
                            handleSizeChange(index, "stock", e.target.value)
                          }
                          placeholder="Stock"
                          className="admin-input"
                          min="0"
                          required
                        />
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveSize(index)}
                            className="admin-remove-btn"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddSize}
                      className="admin-add-sub-btn"
                    >
                      + Add Size Variant
                    </button>
                  </div>

                  {submitMessage.text && (
                    <div className={`admin-toast ${submitMessage.type}`} style={{ marginBottom: "15px" }}>
                      {submitMessage.text}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="admin-submit-gold"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Publishing Product..." : "Publish Product"}
                  </button>
                </form>

                {/* Live Preview Card */}
                <div className="admin-preview-card">
                  <span className="admin-preview-label">LIVE PREVIEW</span>
                  <div className="admin-preview-box">
                    {(() => {
                      const validImages = formData.productImages
                        .filter((img) => img && img.trim() !== "")
                        .map((url) => ({ type: "image", url }));

                      let previewMediaList = [...validImages];
                      if (formData.productVideo && formData.productVideo.trim() !== "") {
                        if (previewMediaList.length > 0) {
                          previewMediaList.splice(1, 0, { type: "video", url: formData.productVideo });
                        } else {
                          previewMediaList.push({ type: "video", url: formData.productVideo });
                        }
                      }

                      const activeIndex =
                        previewMediaList.length > 0
                          ? ((previewImageIndex % previewMediaList.length) +
                              previewMediaList.length) %
                            previewMediaList.length
                          : 0;
                      const currentMedia = previewMediaList[activeIndex];

                      return (
                        <>
                          <div className="admin-preview-image-wrapper">
                            {currentMedia?.type === "video" ? (
                              <video
                                src={currentMedia.url}
                                controls
                                playsInline
                                autoPlay
                                muted
                                loop
                                className="admin-preview-video"
                              />
                            ) : (
                              <img
                                src={currentMedia?.url || "https://placehold.co/400x500?text=Product+Image+Preview"}
                                alt="Preview"
                                onError={(e) => {
                                  e.target.src =
                                    "https://placehold.co/400x500?text=Invalid+Image+URL";
                                }}
                              />
                            )}

                            {/* Counter Badge */}
                            {previewMediaList.length > 1 && (
                              <span className="admin-preview-badge">
                                {currentMedia?.type === "video" ? "🎬 " : ""}
                                {activeIndex + 1} / {previewMediaList.length}
                              </span>
                            )}

                            {/* Arrow Controls */}
                            {previewMediaList.length > 1 && (
                              <>
                                <button
                                  type="button"
                                  className="admin-preview-arrow admin-preview-arrow-prev"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setPreviewImageIndex((prev) =>
                                      prev > 0
                                        ? prev - 1
                                        : previewMediaList.length - 1
                                    );
                                  }}
                                  aria-label="Previous Media"
                                >
                                  ‹
                                </button>
                                <button
                                  type="button"
                                  className="admin-preview-arrow admin-preview-arrow-next"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setPreviewImageIndex((prev) =>
                                      prev < previewMediaList.length - 1
                                        ? prev + 1
                                        : 0
                                    );
                                  }}
                                  aria-label="Next Media"
                                >
                                  ›
                                </button>
                              </>
                            )}

                            {/* Dots Indicator */}
                            {previewMediaList.length > 1 && (
                              <div className="admin-preview-dots">
                                {previewMediaList.map((item, i) => (
                                  <span
                                    key={i}
                                    className={`admin-preview-dot ${
                                      i === activeIndex ? "active" : ""
                                    }`}
                                    onClick={() => setPreviewImageIndex(i)}
                                    title={item.type === "video" ? "Video (Slide 2)" : `Image ${i + 1}`}
                                  />
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Scrollable Thumbnails Strip for all added media */}
                          {previewMediaList.length > 1 && (
                            <div className="admin-preview-thumbs">
                              {previewMediaList.map((item, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  className={`admin-preview-thumb-btn ${
                                    idx === activeIndex ? "active" : ""
                                  }`}
                                  onClick={() => setPreviewImageIndex(idx)}
                                  title={item.type === "video" ? "Video (Slide 2)" : `Thumb ${idx + 1}`}
                                >
                                  {item.type === "video" ? (
                                    <div className="admin-preview-thumb-video-icon">
                                      🎬
                                    </div>
                                  ) : (
                                    <img
                                      src={item.url}
                                      alt={`Thumb ${idx + 1}`}
                                      onError={(e) => {
                                        e.target.src =
                                          "https://placehold.co/60x60?text=Error";
                                      }}
                                    />
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}

                    <div className="admin-preview-info">
                      <h4>{formData.productName || "PRODUCT NAME"}</h4>
                      <p className="admin-preview-price">
                        ₹{formData.productPrice || "0.00"}
                        {formData.beforeDiscountPrice && (
                          <span className="admin-preview-old-price">
                            {" "}
                            ₹{formData.beforeDiscountPrice}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* MODE 2: HOMEPAGE BANNERS (SAFEGUARDED CATEGORY = "Main Image") */}
            {adminMode === "banner" && (
              <div className="admin-content-grid">
                <form className="admin-form-card" onSubmit={handleBannerSubmit}>
                  <div className="admin-card-header">
                    <h2>Upload Homepage Banner</h2>
                    <p>
                       Safeguarded: Category is locked to <strong>Main Image</strong> so this item will never leak into product listings.
                    </p>
                  </div>

                  <div className="admin-form-group">
                    <label>Overlay Headline (Watermark Text)</label>
                    <input
                      type="text"
                      value={bannerData.headline}
                      onChange={(e) =>
                        setBannerData({ ...bannerData, headline: e.target.value })
                      }
                      placeholder="e.g. AMINII LUXE COLLECTION (Leave blank for default)"
                      className="admin-input"
                    />
                  </div>

                  {/* Desktop Banner Upload */}
                  <div className="admin-form-group">
                    <label>
                      Desktop Banner Image (1920x700px) *
                      <span className="admin-box-subtitle">
                        {" "}Uploads directly to Cloudflare R2 and is automatically formatted to landscape 1920x700px.
                      </span>
                    </label>

                    {/* Upload Dropzone */}
                    <div
                      className="admin-upload-dropzone"
                      style={{ padding: "16px", minHeight: "80px" }}
                      onClick={() => desktopBannerInputRef.current?.click()}
                    >
                      <input
                        ref={desktopBannerInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleBannerFileUpload(file, "desktop");
                          e.target.value = "";
                        }}
                      />
                      <div className="admin-upload-dropzone-inner">
                        <span className="admin-upload-icon" style={{ fontSize: "20px", marginBottom: "4px" }}>🖥️</span>
                        <p><strong>Click to upload Desktop Banner</strong> (JPG, PNG, WebP)</p>
                      </div>
                    </div>

                    {/* Desktop Upload State / Preview */}
                    {desktopBannerUploadState.status === "uploading" && (
                      <div style={{ marginTop: "10px" }}>
                        <div style={{ background: "rgba(212, 175, 55, 0.1)", borderRadius: "4px", overflow: "hidden", height: "6px" }}>
                          <div style={{ width: `${desktopBannerUploadState.progress}%`, height: "100%", background: "var(--admin-gold)" }} />
                        </div>
                        <span style={{ fontSize: "11px", color: "var(--admin-gold)" }}>Uploading: {desktopBannerUploadState.progress}%</span>
                      </div>
                    )}

                    {bannerData.desktopImageUrl && (
                      <div style={{ marginTop: "10px", position: "relative", display: "inline-block" }}>
                        <img
                          src={bannerData.desktopImageUrl}
                          alt="Desktop Banner Preview"
                          style={{ maxHeight: "120px", width: "100%", maxWidth: "360px", objectFit: "cover", borderRadius: "6px", border: "1px solid var(--admin-gold)" }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setBannerData((prev) => ({ ...prev, desktopImageUrl: "" }));
                            setDesktopBannerUploadState({ status: "idle", progress: 0, error: null });
                          }}
                          className="admin-upload-thumb-remove"
                          style={{ position: "absolute", top: "-8px", right: "-8px" }}
                          title="Remove Desktop Banner"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Mobile Banner Upload */}
                  <div className="admin-form-group">
                    <label>
                      Mobile Banner Image (800x1100px) (Optional)
                      <span className="admin-box-subtitle">
                        {" "}Upload portrait mobile banner. Defaults to desktop banner if left blank.
                      </span>
                    </label>

                    {/* Upload Dropzone */}
                    <div
                      className="admin-upload-dropzone"
                      style={{ padding: "16px", minHeight: "80px" }}
                      onClick={() => mobileBannerInputRef.current?.click()}
                    >
                      <input
                        ref={mobileBannerInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleBannerFileUpload(file, "mobile");
                          e.target.value = "";
                        }}
                      />
                      <div className="admin-upload-dropzone-inner">
                        <span className="admin-upload-icon" style={{ fontSize: "20px", marginBottom: "4px" }}>📱</span>
                        <p><strong>Click to upload Mobile Banner</strong> (JPG, PNG, WebP)</p>
                      </div>
                    </div>

                    {/* Mobile Upload State / Preview */}
                    {mobileBannerUploadState.status === "uploading" && (
                      <div style={{ marginTop: "10px" }}>
                        <div style={{ background: "rgba(212, 175, 55, 0.1)", borderRadius: "4px", overflow: "hidden", height: "6px" }}>
                          <div style={{ width: `${mobileBannerUploadState.progress}%`, height: "100%", background: "var(--admin-gold)" }} />
                        </div>
                        <span style={{ fontSize: "11px", color: "var(--admin-gold)" }}>Uploading: {mobileBannerUploadState.progress}%</span>
                      </div>
                    )}

                    {bannerData.mobileImageUrl && (
                      <div style={{ marginTop: "10px", position: "relative", display: "inline-block" }}>
                        <img
                          src={bannerData.mobileImageUrl}
                          alt="Mobile Banner Preview"
                          style={{ maxHeight: "140px", width: "100px", objectFit: "cover", borderRadius: "6px", border: "1px solid var(--admin-gold)" }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setBannerData((prev) => ({ ...prev, mobileImageUrl: "" }));
                            setMobileBannerUploadState({ status: "idle", progress: 0, error: null });
                          }}
                          className="admin-upload-thumb-remove"
                          style={{ position: "absolute", top: "-8px", right: "-8px" }}
                          title="Remove Mobile Banner"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="admin-form-group">
                    <label>Target Category Link (When User Clicks Banner) *</label>
                    <select
                      value={bannerData.targetCategorySlug}
                      onChange={(e) =>
                        setBannerData({
                          ...bannerData,
                          targetCategorySlug: e.target.value,
                        })
                      }
                      className="admin-input"
                    >
                      {bannerCategoryOptions.map((opt, i) => (
                        <option key={i} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {submitMessage.text && (
                    <div className={`admin-toast ${submitMessage.type}`} style={{ marginBottom: "15px" }}>
                      {submitMessage.text}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="admin-submit-gold"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Publishing Banner..." : "Publish Banner"}
                  </button>
                </form>

                {/* Banner Management List */}
                <div className="admin-list-card">
                  <div className="admin-card-header">
                    <h2>Active Homepage Banners</h2>
                    <p>{existingBanners.length} banners live in slider</p>
                  </div>

                  <div className="admin-banner-list">
                    {existingBanners.map((b) => (
                      <div key={b.productId} className="admin-banner-item">
                        <img
                          src={b.productImages?.[0] || ""}
                          alt="Banner"
                          className="admin-banner-thumb"
                        />
                        <div className="admin-banner-info">
                          <h5>{b.productName}</h5>
                          <p>
                            Links to:{" "}
                            <code>
                              {b.productCategory?.[0]?.subCategory?.categoryName || "store"}
                            </code>
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteBanner(b.productId)}
                          className="admin-delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                    {existingBanners.length === 0 && (
                      <p className="admin-empty-text">No active banners found.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* MODE 3: REELS & LOOKBOOK */}
            {adminMode === "reel" && (
              <div className="admin-content-grid">
                <form className="admin-form-card" onSubmit={handleReelSubmit}>
                  <div className="admin-card-header">
                    <h2>Publish New Reel / Lookbook Video</h2>
                    <p>Add 9:16 short video reels with Instagram link & Shop Now trigger</p>
                  </div>

                  <div className="admin-form-group">
                    <label>Reel Title (Optional)</label>
                    <input
                      type="text"
                      value={reelData.title}
                      onChange={(e) =>
                        setReelData({ ...reelData, title: e.target.value })
                      }
                      placeholder="e.g. Summer Pakistani Luxe Collection"
                      className="admin-input"
                    />
                  </div>

                  {/* Video Upload Dropzone */}
                  <div className="admin-form-group">
                    <label>Reel Video (.mp4 / .mov / .webm) *</label>

                    <div
                      className={`admin-upload-dropzone ${isReelDragOver ? "drag-over" : ""}`}
                      onClick={() => reelVideoInputRef.current?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsReelDragOver(true);
                      }}
                      onDragLeave={() => setIsReelDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsReelDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) handleReelVideoUpload(file);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        ref={reelVideoInputRef}
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm,video/x-m4v,video/*"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleReelVideoUpload(file);
                          e.target.value = "";
                        }}
                      />
                      <div className="admin-upload-dropzone-inner">
                        <span className="admin-upload-icon" style={{ fontSize: "28px", marginBottom: "6px" }}>🎬</span>
                        <p><strong>Click to upload or drag & drop video</strong></p>
                        <p style={{ fontSize: "12px", opacity: 0.8, marginTop: "4px" }}>
                          9:16 Vertical Reel format recommended (MP4, MOV, WebM - up to 100MB)
                        </p>
                      </div>
                    </div>

                    {/* Upload Progress */}
                    {reelVideoUploadState.status === "uploading" && (
                      <div style={{ marginTop: "12px" }}>
                        <div style={{ background: "rgba(212, 175, 55, 0.15)", borderRadius: "6px", overflow: "hidden", height: "8px" }}>
                          <div
                            style={{
                              width: `${reelVideoUploadState.progress}%`,
                              height: "100%",
                              background: "linear-gradient(90deg, #b8860b, #d4af37)",
                              transition: "width 0.2s ease",
                            }}
                          />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px", fontSize: "12px", color: "var(--admin-gold)" }}>
                          <span>Uploading video to Cloudflare R2...</span>
                          <span>{reelVideoUploadState.progress}%</span>
                        </div>
                      </div>
                    )}

                    {/* Upload Success Indicator */}
                    {reelVideoUploadState.status === "done" && reelData.videoUrl && (
                      <div style={{ marginTop: "8px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(46, 204, 113, 0.1)", border: "1px solid rgba(46, 204, 113, 0.3)", borderRadius: "6px", padding: "8px 12px" }}>
                        <span style={{ fontSize: "12px", color: "#2ecc71", display: "flex", alignItems: "center", gap: "6px" }}>
                          ✓ Video uploaded successfully & ready to loop
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setReelData((prev) => ({ ...prev, videoUrl: "" }));
                            setReelVideoUploadState({ status: "idle", progress: 0, error: null });
                          }}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#e74c3c",
                            fontSize: "12px",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          Remove / Change
                        </button>
                      </div>
                    )}

                    {/* Toggle for Manual URL (Fallback) */}
                    <div style={{ marginTop: "10px" }}>
                      <button
                        type="button"
                        onClick={() => setShowManualVideoUrl((prev) => !prev)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--admin-gold, #d4af37)",
                          fontSize: "12px",
                          cursor: "pointer",
                          textDecoration: "underline",
                          padding: 0,
                        }}
                      >
                        {showManualVideoUrl ? "▲ Hide manual URL field" : "▼ Or enter direct video URL manually"}
                      </button>
                    </div>

                    {showManualVideoUrl && (
                      <div style={{ marginTop: "8px" }}>
                        <input
                          type="url"
                          value={reelData.videoUrl}
                          onChange={(e) => {
                            setReelData({ ...reelData, videoUrl: e.target.value });
                            setReelVideoUploadState({ status: "idle", progress: 0, error: null });
                          }}
                          placeholder="https://.../video.mp4"
                          className="admin-input"
                        />
                      </div>
                    )}
                  </div>

                  <div className="admin-form-group">
                    <label>Instagram Link (URL of post / profile)</label>
                    <input
                      type="url"
                      value={reelData.instagramUrl}
                      onChange={(e) =>
                        setReelData({ ...reelData, instagramUrl: e.target.value })
                      }
                      placeholder="https://www.instagram.com/reel/Dazni4mIq0A/"
                      className="admin-input"
                    />
                  </div>

                  {/* Mutually Exclusive Link Choice */}
                  <div className="admin-form-group">
                    <label>Shop Now Link Type *</label>
                    <div className="admin-radio-group">
                      <label className="admin-radio-label">
                        <input
                          type="radio"
                          name="linkType"
                          value="category"
                          checked={reelData.linkType === "category"}
                          onChange={() =>
                            setReelData({
                              ...reelData,
                              linkType: "category",
                              targetValue: "aminii-luxe",
                            })
                          }
                        />
                        Link to Category
                      </label>
                      <label className="admin-radio-label">
                        <input
                          type="radio"
                          name="linkType"
                          value="product"
                          checked={reelData.linkType === "product"}
                          onChange={() =>
                            setReelData({
                              ...reelData,
                              linkType: "product",
                              targetValue: "",
                            })
                          }
                        />
                        Link to Specific Product ID
                      </label>
                    </div>
                  </div>

                  {reelData.linkType === "category" ? (
                    <div className="admin-form-group">
                      <label>Target Category</label>
                      <select
                        value={reelData.targetValue}
                        onChange={(e) =>
                          setReelData({ ...reelData, targetValue: e.target.value })
                        }
                        className="admin-input"
                      >
                        {bannerCategoryOptions.map((opt, i) => (
                          <option key={i} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="admin-form-group">
                      <label>Target Product ID (e.g. 19)</label>
                      <input
                        type="number"
                        value={reelData.targetValue}
                        onChange={(e) =>
                          setReelData({ ...reelData, targetValue: e.target.value })
                        }
                        placeholder="Enter product ID (e.g. 19)"
                        className="admin-input"
                        required
                      />
                    </div>
                  )}

                  {/* Live Reel Preview */}
                  {reelData.videoUrl && (
                    <div className="admin-reel-preview-card">
                      <p className="admin-reel-preview-label">🎬 Live Preview</p>
                      {/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(reelData.videoUrl) || (typeof reelData.videoUrl === "string" && reelData.videoUrl.includes("/video/upload/")) ? (
                        <video
                          src={reelData.videoUrl}
                          controls
                          muted
                          loop
                          className="admin-reel-preview-video"
                        />
                      ) : (
                        <div className="admin-reel-preview-instagram">
                          <div className="admin-reel-preview-ig-icon">📸</div>
                          <p className="admin-reel-preview-ig-title">
                            {reelData.title || "Aminii Lookbook Reel"}
                          </p>
                          <p className="admin-reel-preview-ig-note">
                            Instagram reels cannot be embedded directly in the browser.<br />
                            The reel will open in a new tab when users tap the 📸 button.
                          </p>
                          <a
                            href={reelData.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="admin-reel-preview-ig-link"
                          >
                            Open Reel on Instagram ↗
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="admin-submit-gold"
                    disabled={isSubmitting || reelVideoUploadState.status === "uploading"}
                  >
                    {reelVideoUploadState.status === "uploading"
                      ? `Uploading Video (${reelVideoUploadState.progress}%)...`
                      : isSubmitting
                      ? "Publishing Reel..."
                      : "Publish Reel"}
                  </button>
                </form>

                {/* Reels List Manager */}
                <div className="admin-list-card">
                  <div className="admin-card-header">
                    <h2>Active Reels</h2>
                    <p>{existingReels.length} reels published</p>
                  </div>

                  <div className="admin-banner-list">
                    {existingReels.map((r) => (
                      <div key={r.id} className="admin-banner-item">
                        <video
                          src={r.videoUrl}
                          className="admin-banner-thumb"
                          muted
                          style={{ objectFit: "cover" }}
                        />
                        <div className="admin-banner-info">
                          <h5>{r.title || "Lookbook Reel"}</h5>
                          <p>
                            Target:{" "}
                            <code>
                              {r.productId ? `Product #${r.productId}` : r.categorySlug}
                            </code>
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteReel(r.id)}
                          className="admin-delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                    {existingReels.length === 0 && (
                      <p className="admin-empty-text">No active reels published.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* MODE 4: MANAGE ORDERS */}
            {adminMode === "orders" && (
              <div className="admin-content-grid" style={{ gridTemplateColumns: "1fr" }}>
                <div className="admin-list-card" style={{ maxWidth: "100%" }}>
                  <div className="admin-orders-header">
                    <div>
                      <h2>Customer Orders & Shiprocket Fulfillment</h2>
                      <p>{adminOrders.length} total orders found</p>
                    </div>
                    <button
                      onClick={fetchAdminOrders}
                      disabled={loadingOrders}
                      className="admin-refresh-btn"
                    >
                      {loadingOrders ? "Refreshing..." : " Refresh"}
                    </button>
                  </div>

                  {/* SEARCH & PRODUCT ID LOOKUP BAR */}
                  <div className="admin-orders-controls-bar" style={{ display: "flex", gap: "12px", alignItems: "center", margin: "16px 0", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 320px", position: "relative" }}>
                      <input
                        type="text"
                        placeholder="🔍 Search orders by Product ID, Order ID, Customer, Phone, or SKU..."
                        value={orderSearchQuery}
                        onChange={(e) => {
                          setOrderSearchQuery(e.target.value);
                          setOrdersPage(1);
                        }}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          background: "#141518",
                          border: "1px solid rgba(212, 175, 55, 0.3)",
                          borderRadius: "6px",
                          color: "#fff",
                          fontSize: "13px"
                        }}
                      />
                      {orderSearchQuery && (
                        <button
                          onClick={() => setOrderSearchQuery("")}
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "transparent",
                            border: "none",
                            color: "#a0a0aa",
                            cursor: "pointer",
                            fontSize: "14px"
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleLookupProduct();
                      }}
                      style={{ display: "flex", gap: "8px", alignItems: "center" }}
                    >
                      <input
                        type="number"
                        placeholder="Enter Product ID (e.g. 104)"
                        value={productLookupId}
                        onChange={(e) => setProductLookupId(e.target.value)}
                        style={{
                          width: "190px",
                          padding: "10px 12px",
                          background: "#141518",
                          border: "1px solid rgba(212, 175, 55, 0.3)",
                          borderRadius: "6px",
                          color: "#fff",
                          fontSize: "13px"
                        }}
                      />
                      <button
                        type="submit"
                        disabled={loadingProductLookup || !productLookupId}
                        style={{
                          padding: "10px 16px",
                          background: "var(--admin-gold, #d4af37)",
                          color: "#000",
                          fontWeight: "600",
                          border: "none",
                          borderRadius: "6px",
                          cursor: loadingProductLookup || !productLookupId ? "not-allowed" : "pointer",
                          fontSize: "13px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px"
                        }}
                      >
                        {loadingProductLookup ? "Fetching..." : "📦 Inspect Product"}
                      </button>
                    </form>
                  </div>

                  {productLookupError && (
                    <div style={{ padding: "8px 12px", background: "rgba(239, 68, 68, 0.15)", border: "1px solid rgba(239, 68, 68, 0.4)", borderRadius: "6px", color: "#fca5a5", fontSize: "13px", marginBottom: "12px" }}>
                      ⚠️ {productLookupError}
                    </div>
                  )}

                  {loadingOrders ? (
                    <p className="admin-empty-text">Loading orders...</p>
                  ) : filteredOrders.length === 0 ? (
                    <p className="admin-empty-text">
                      {orderSearchQuery ? `No orders found matching "${orderSearchQuery}".` : "No orders found."}
                    </p>
                  ) : (() => {
                    const totalPages = Math.ceil(filteredOrders.length / ORDERS_PER_PAGE) || 1;
                    const paginatedOrders = filteredOrders.slice((ordersPage - 1) * ORDERS_PER_PAGE, ordersPage * ORDERS_PER_PAGE);

                    return (
                      <>
                        {/* DESKTOP TABLE VIEW */}
                        <div className="admin-orders-desktop-table">
                          <div className="admin-orders-table-wrapper">
                            <table className="admin-orders-table">
                              <thead>
                                <tr>
                                  <th>Order ID</th>
                                  <th>Customer</th>
                                  <th>Date</th>
                                  <th>Amount</th>
                                  <th>Payment</th>
                                  <th>Status</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {paginatedOrders.map((ord) => {
                                  const status = (ord.orderStatus || "ORDER_PLACED").toUpperCase();
                                  const isPlaced = status === "ORDER_PLACED" || status === "PENDING";
                                  const isConfirmed = status === "ORDER_CONFIRMED";
                                  const canCancel = isPlaced || isConfirmed;
                                  const hasLabel = Boolean(ord.shiprocket?.labelUrl || status === "READY_TO_SHIP" || status === "IN_TRANSIT" || status === "OUT_FOR_DELIVERY" || status === "DELIVERED");
                            const hasManifest = Boolean(ord.manifestUrl || ord.shiprocket?.manifestUrl || ord.shiprocket?.awbCode || status === "READY_TO_SHIP" || status === "IN_TRANSIT" || status === "OUT_FOR_DELIVERY" || status === "DELIVERED");

                                  return (
                                    <tr key={ord.orderId}>
                                      <td style={{ fontWeight: "600", color: "#d4af37" }}>#{ord.orderId}</td>
                                      <td className="customer-cell">
                                        <strong style={{ color: "#ffffff" }}>{ord.customerName || "Customer"}</strong>
                                        <br />
                                        <span style={{ fontSize: "12px", color: "#a0a0aa" }}>{ord.customerPhone}</span>
                                      </td>
                                      <td style={{ padding: "12px", color: "#e4e4e7" }}>{ord.orderDate}</td>
                                      <td style={{ padding: "12px", fontWeight: "600", color: "#ffffff" }}>₹{ord.totalPrice}</td>
                                      <td style={{ padding: "12px" }}>
                                        <span style={{ fontSize: "12px", fontWeight: "600", textTransform: "uppercase", color: "#e4e4e7" }}>
                                          {ord.paymentMethod || ord.paymentType}
                                        </span>
                                      </td>
                                      <td style={{ padding: "12px" }}>
                                        <span className={`order-status-badge badge-${status.toLowerCase()}`}>
                                          {status === "SHIPROCKET_FAILED" ? "SHIPROCKET_FAILED" : status}
                                        </span>
                                      </td>
                                      <td style={{ padding: "12px" }}>
                                        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                          {isPlaced && (
                                            <button
                                              onClick={() => setConfirmModalOrder(ord)}
                                              disabled={actionLoading}
                                              style={{ padding: "6px 14px", background: "#16a34a", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "600" }}
                                            >
                                              Mark Confirmed
                                            </button>
                                          )}

                                          {isConfirmed && (
                                            <button
                                              onClick={() => handleOpenOrderDetails(ord)}
                                              disabled={actionLoading}
                                              style={{ padding: "6px 14px", background: "#ea580c", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "600" }}
                                            >
                                              Ready to Ship
                                            </button>
                                          )}

                                          {ord.orderStatus === "SHIPROCKET_FAILED" && (
                                            <button
                                              onClick={() => handleOpenOrderDetails(ord)}
                                              disabled={actionLoading}
                                              style={{ padding: "6px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "600" }}
                                            >
                                              Retry Shiprocket 🚀
                                            </button>
                                          )}

                                          {hasLabel && (
                                            <button
                                              onClick={() => handleDownloadLabel(ord.orderId, ord.shiprocket?.labelUrl)}
                                              disabled={actionLoading}
                                              style={{ padding: "6px 14px", background: "#0284c7", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "600" }}
                                            >
                                              🏷️ Label
                                            </button>
                                          )}

                                          {hasManifest && (
                                            <button
                                              onClick={() => handleDownloadManifest(ord.orderId, ord.manifestUrl || ord.shiprocket?.manifestUrl)}
                                              disabled={actionLoading}
                                              style={{ padding: "6px 14px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "600" }}
                                            >
                                              📄 Manifest
                                            </button>
                                          )}

                                          {canCancel && (
                                            <button
                                              onClick={() => setCancelModalOrder(ord)}
                                              disabled={actionLoading}
                                              style={{ padding: "6px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "600" }}
                                            >
                                              Cancel
                                            </button>
                                          )}

                                          <button
                                            onClick={() => handleOpenOrderDetails(ord)}
                                            title="View Order Details"
                                            aria-label="View Order Details"
                                            style={{ padding: "6px 10px", background: "#1e293b", color: "#d4af37", border: "1px solid rgba(212,175,55,0.3)", borderRadius: "4px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                                          >
                                            <svg width="18" height="12" viewBox="0 0 24 16" fill="currentColor">
                                              <path d="M12 0C6.5 0 1.8 3.5 0 8c1.8 4.5 6.5 8 12 8s10.2-3.5 12-8c-1.8-4.5-6.5-8-12-8zm0 13c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5zm0-8c-1.7 0-3 1.3-3 3s1.3 3 3 3 3-1.3 3-3-1.3-3-3-3z"/>
                                            </svg>
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* MOBILE ORDER CARDS VIEW */}
                        <div className="admin-orders-mobile-cards">
                          {paginatedOrders.map((ord) => {
                            const status = (ord.orderStatus || "ORDER_PLACED").toUpperCase();
                            const isPlaced = status === "ORDER_PLACED" || status === "PENDING";
                            const isConfirmed = status === "ORDER_CONFIRMED";
                            const canCancel = isPlaced || isConfirmed;
                            const hasLabel = Boolean(ord.shiprocket?.labelUrl || status === "READY_TO_SHIP" || status === "IN_TRANSIT" || status === "OUT_FOR_DELIVERY" || status === "DELIVERED");
                            const hasManifest = Boolean(ord.manifestUrl || ord.shiprocket?.manifestUrl || ord.shiprocket?.awbCode || status === "READY_TO_SHIP" || status === "IN_TRANSIT" || status === "OUT_FOR_DELIVERY" || status === "DELIVERED");

                            return (
                              <div className="admin-mobile-order-card" key={ord.orderId}>
                                <div className="admin-mobile-card-top">
                                  <div className="admin-mobile-order-id">#{ord.orderId}</div>
                                  <span className={`order-status-badge badge-${status.toLowerCase()}`}>
                                    {status === "SHIPROCKET_FAILED" ? "SHIPROCKET_FAILED" : status}
                                  </span>
                                </div>

                                <div className="admin-mobile-card-body">
                                  <div className="admin-mobile-detail-row">
                                    <span className="admin-mobile-label">👤 Customer:</span>
                                    <span className="admin-mobile-value">
                                      <strong>{ord.customerName || "Customer"}</strong> ({ord.customerPhone || "N/A"})
                                    </span>
                                  </div>

                                  <div className="admin-mobile-detail-row">
                                    <span className="admin-mobile-label">📅 Date:</span>
                                    <span className="admin-mobile-value">{ord.orderDate}</span>
                                  </div>

                                  <div className="admin-mobile-detail-row">
                                    <span className="admin-mobile-label">💰 Amount:</span>
                                    <span className="admin-mobile-value gold-price">₹{ord.totalPrice}</span>
                                  </div>

                                  <div className="admin-mobile-detail-row">
                                    <span className="admin-mobile-label">💳 Payment:</span>
                                    <span className="admin-mobile-value">{ord.paymentMethod || ord.paymentType}</span>
                                  </div>
                                </div>

                                <div className="admin-mobile-card-actions">
                                  {isPlaced && (
                                    <button
                                      onClick={() => setConfirmModalOrder(ord)}
                                      disabled={actionLoading}
                                      className="admin-btn-confirm"
                                    >
                                      Mark Confirmed
                                    </button>
                                  )}

                                  {isConfirmed && (
                                    <button
                                      onClick={() => handleOpenOrderDetails(ord)}
                                      disabled={actionLoading}
                                      className="admin-btn-ship"
                                    >
                                      Ready to Ship
                                    </button>
                                  )}

                                  {ord.orderStatus === "SHIPROCKET_FAILED" && (
                                    <button
                                      onClick={() => handleOpenOrderDetails(ord)}
                                      disabled={actionLoading}
                                      className="admin-btn-ship"
                                      style={{ background: "#dc2626" }}
                                    >
                                      Retry Shiprocket 🚀
                                    </button>
                                  )}

                                  {hasLabel && (
                                    <button
                                      onClick={() => handleDownloadLabel(ord.orderId, ord.shiprocket?.labelUrl)}
                                      disabled={actionLoading}
                                      className="admin-btn-label"
                                    >
                                      🏷️ Label
                                    </button>
                                  )}

                                  {hasManifest && (
                                    <button
                                      onClick={() => handleDownloadManifest(ord.orderId, ord.manifestUrl || ord.shiprocket?.manifestUrl)}
                                      disabled={actionLoading}
                                      className="admin-btn-manifest"
                                    >
                                      📄 Manifest
                                    </button>
                                  )}

                                  {canCancel && (
                                    <button
                                      onClick={() => setCancelModalOrder(ord)}
                                      disabled={actionLoading}
                                      className="admin-btn-cancel"
                                    >
                                      Cancel
                                    </button>
                                  )}

                                  <button
                                    onClick={() => handleOpenOrderDetails(ord)}
                                    className="admin-btn-view"
                                  >
                                    👁️ View Details
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* PAGINATION CONTROLS */}
                        {totalPages > 1 && (
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px", paddingTop: "16px", borderTop: "1px solid rgba(255, 255, 255, 0.08)", flexWrap: "wrap", gap: "12px" }}>
                            <div style={{ fontSize: "13px", color: "#a0a0aa" }}>
                              Showing {((ordersPage - 1) * ORDERS_PER_PAGE) + 1} to {Math.min(ordersPage * ORDERS_PER_PAGE, filteredOrders.length)} of {filteredOrders.length} orders
                            </div>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                              <button
                                onClick={() => setOrdersPage((prev) => Math.max(prev - 1, 1))}
                                disabled={ordersPage === 1}
                                style={{ padding: "6px 14px", background: ordersPage === 1 ? "#1e293b" : "#d4af37", color: ordersPage === 1 ? "#64748b" : "#0b0c0e", border: "none", borderRadius: "4px", cursor: ordersPage === 1 ? "not-allowed" : "pointer", fontWeight: "600", fontSize: "13px" }}
                              >
                                Previous
                              </button>
                              <span style={{ fontSize: "14px", color: "#f3e5ab", fontWeight: "600", padding: "0 8px" }}>
                                Page {ordersPage} of {totalPages}
                              </span>
                              <button
                                onClick={() => setOrdersPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={ordersPage === totalPages}
                                style={{ padding: "6px 14px", background: ordersPage === totalPages ? "#1e293b" : "#d4af37", color: ordersPage === totalPages ? "#64748b" : "#0b0c0e", border: "none", borderRadius: "4px", cursor: ordersPage === totalPages ? "not-allowed" : "pointer", fontWeight: "600", fontSize: "13px" }}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* ORDER DETAILS MODAL */}
            {selectedOrder && (
              <div className="checkout-confirm-overlay" onClick={() => setSelectedOrder(null)} style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }}>
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: "650px", width: "90%", background: "#141518", border: "1px solid rgba(212, 175, 55, 0.3)", borderRadius: "12px", padding: "24px", color: "#f3f4f6", boxShadow: "0 10px 40px rgba(0,0,0,0.8)", textAlign: "left" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "12px" }}>
                    <h3 style={{ margin: 0, fontFamily: "var(--font-heading)", color: "#f3e5ab", fontSize: "22px" }}>Order #{selectedOrder.orderId} Details</h3>
                    <button onClick={() => setSelectedOrder(null)} style={{ background: "none", border: "none", fontSize: "22px", color: "#d4af37", cursor: "pointer" }}>✕</button>
                  </div>
                  <div className="admin-custom-scroll" style={{ maxHeight: "70vh", overflowY: "auto", fontSize: "14px", lineHeight: "1.6", paddingRight: "10px" }}>
                    <p style={{ margin: "6px 0" }}><strong style={{ color: "#d4af37" }}>Customer:</strong> {selectedOrder.customerName} ({selectedOrder.customerPhone || "N/A"}, {selectedOrder.customerEmail || "N/A"})</p>
                    <p style={{ margin: "6px 0" }}><strong style={{ color: "#d4af37" }}>Address:</strong> {selectedOrder.customerAddress?.street}, {selectedOrder.customerAddress?.city}, {selectedOrder.customerAddress?.state} - {selectedOrder.customerAddress?.pincode}</p>
                    <p style={{ margin: "6px 0" }}><strong style={{ color: "#d4af37" }}>Payment Method:</strong> {selectedOrder.paymentMethod || selectedOrder.paymentType} ({selectedOrder.paymentStatus})</p>

                    {selectedOrder.shiprocket && (
                      <div style={{ background: "#1a1b20", border: "1px solid #2c2d35", padding: "14px", borderRadius: "8px", margin: "14px 0", color: "#f3f4f6" }}>
                        <p style={{ margin: 0, fontWeight: "700", color: "#f3e5ab" }}>Shiprocket Fulfillment Info:</p>
                        <p style={{ margin: "4px 0" }}>AWB Code: <strong>{selectedOrder.shiprocket.awbCode || selectedOrder.trackingId || "N/A"}</strong></p>
                        <p style={{ margin: "4px 0" }}>Courier Partner: <strong>{selectedOrder.shiprocket.courierName || selectedOrder.courierName || "N/A"}</strong></p>
                        {selectedOrder.shiprocket.trackingUrl && (
                          <a href={selectedOrder.shiprocket.trackingUrl} target="_blank" rel="noreferrer" style={{ color: "#60a5fa", textDecoration: "underline", fontWeight: "600", display: "inline-block", marginTop: "4px" }}>
                            Track your Order ↗
                          </a>
                        )}
                        <div style={{ display: "flex", gap: "10px", marginTop: "12px", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={() => handleDownloadLabel(selectedOrder.orderId, selectedOrder.shiprocket?.labelUrl)}
                            disabled={actionLoading}
                            style={{
                              padding: "7px 14px",
                              background: "linear-gradient(135deg, #0284c7, #0369a1)",
                              color: "#ffffff",
                              border: "none",
                              borderRadius: "6px",
                              fontSize: "13px",
                              fontWeight: "600",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              boxShadow: "0 2px 8px rgba(2, 132, 199, 0.3)"
                            }}
                          >
                            🏷️ Download Label
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownloadManifest(selectedOrder.orderId, selectedOrder.manifestUrl || selectedOrder.shiprocket?.manifestUrl)}
                            disabled={actionLoading}
                            style={{
                              padding: "7px 14px",
                              background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                              color: "#ffffff",
                              border: "none",
                              borderRadius: "6px",
                              fontSize: "13px",
                              fontWeight: "600",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              boxShadow: "0 2px 8px rgba(124, 58, 237, 0.3)"
                            }}
                          >
                            📄 Download Manifest
                          </button>
                        </div>
                      </div>
                    )}

                    {selectedOrder.cancellation?.reason && (
                      <p style={{ color: "#f87171", background: "rgba(239,68,68,0.1)", padding: "10px", borderRadius: "6px", margin: "10px 0" }}>
                        <strong>Cancellation Reason:</strong> {selectedOrder.cancellation.reason}
                      </p>
                    )}

                    <h4 style={{ margin: "16px 0 8px", color: "#f3e5ab" }}>ORDERED ITEMS:</h4>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", margin: "10px 0" }}>
                      {selectedOrder.orderedProducts?.map((item, idx) => (
                        <div
                          key={idx}
                          style={{
                            background: "#1a1b20",
                            border: "1px solid rgba(212, 175, 55, 0.2)",
                            borderRadius: "8px",
                            padding: "12px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "8px"
                          }}
                        >
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span style={{ fontWeight: "700", color: "#ffffff", fontSize: "15px" }}>{item.productName}</span>
                              {item.productId && (
                                <span style={{ background: "rgba(212, 175, 55, 0.15)", color: "#f3e5ab", padding: "2px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: "600" }}>
                                  ID: #{item.productId}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: "12px", color: "#a0a0aa", marginTop: "4px" }}>
                              <span style={{ marginRight: "12px" }}>
                                Size:{" "}
                                <strong
                                  style={{
                                    color: item.size ? "#e4e4e7" : "#fca5a5",
                                    background: item.size ? "rgba(255,255,255,0.08)" : "rgba(239,68,68,0.15)",
                                    padding: "1px 6px",
                                    borderRadius: "3px",
                                  }}
                                >
                                  {item.size || "Not recorded"}
                                </strong>
                              </span>
                              {item.sku && <span style={{ marginRight: "12px" }}>SKU: <strong style={{ color: "#e4e4e7" }}>{item.sku}</strong></span>}
                              <span>Qty: <strong style={{ color: "#e4e4e7" }}>{item.quantity}</strong> × ₹{item.unitPrice || 0}</span>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <span style={{ fontWeight: "700", color: "#d4af37", fontSize: "15px" }}>
                              ₹{item.totalProductPrice || (item.unitPrice * item.quantity)}
                            </span>
                            {item.productId && (
                              <button
                                onClick={() => handleLookupProduct(item.productId, item.size)}
                                style={{
                                  padding: "5px 10px",
                                  background: "#2c2d35",
                                  border: "1px solid rgba(212, 175, 55, 0.3)",
                                  borderRadius: "4px",
                                  color: "#f3e5ab",
                                  fontSize: "12px",
                                  cursor: "pointer",
                                  fontWeight: "600"
                                }}
                              >
                                📦 Inspect Product
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* ORDER DIMENSIONS SECTION */}
                    <div style={{ marginTop: "20px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                        <h4 style={{ margin: 0, color: "#f3e5ab", fontSize: "15px", letterSpacing: "0.5px" }}>ORDER DIMENSIONS:</h4>
                        {(selectedOrder.orderStatus === "ORDER_CONFIRMED" || selectedOrder.orderStatus === "SHIPROCKET_FAILED") && (
                          <button
                            type="button"
                            onClick={() => setIsEditingDimensions(true)}
                            style={{
                              padding: "4px 12px",
                              background: "#2c2d35",
                              border: "1px solid rgba(212, 175, 55, 0.4)",
                              borderRadius: "4px",
                              color: "#f3e5ab",
                              fontSize: "12px",
                              cursor: "pointer",
                              fontWeight: "600",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px"
                            }}
                          >
                            ✏️ Edit Dimensions
                          </button>
                        )}
                      </div>

                      <div
                        style={{
                          background: "#1a1b20",
                          border: "1px solid rgba(212, 175, 55, 0.25)",
                          borderRadius: "8px",
                          padding: "14px 16px",
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                          gap: "12px",
                          alignItems: "center"
                        }}
                      >
                        <div>
                          <span style={{ fontSize: "11px", color: "#a0a0aa", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "2px" }}>
                            Total Weight
                          </span>
                          <strong style={{ color: "#d4af37", fontSize: "15px" }}>{shipWeight} KG</strong>
                        </div>
                        <div>
                          <span style={{ fontSize: "11px", color: "#a0a0aa", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "2px" }}>
                            Box Dimensions
                          </span>
                          <strong style={{ color: "#ffffff", fontSize: "14px" }}>{shipLength} × {shipWidth} × {shipHeight} cm</strong>
                        </div>
                        <div>
                          <span style={{ fontSize: "11px", color: "#a0a0aa", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "2px" }}>
                            Pickup Warehouse
                          </span>
                          <strong style={{ color: "#ffffff", fontSize: "14px" }}>{shipPickupLocation || "Aminii clothing "}</strong>
                        </div>
                      </div>
                    </div>

                    {/* READY TO SHIP ACTION BAR */}
                    {(selectedOrder.orderStatus === "ORDER_CONFIRMED" || selectedOrder.orderStatus === "SHIPROCKET_FAILED") && (
                      <div
                        style={{
                          marginTop: "20px",
                          paddingTop: "16px",
                          borderTop: "1px solid rgba(255,255,255,0.1)",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "12px"
                        }}
                      >
                        <span style={{ fontSize: "13px", color: "#a0a0aa" }}>
                          Dispatches at: <strong style={{ color: "#d4af37" }}>{shipWeight} KG</strong> ({shipLength}×{shipWidth}×{shipHeight} cm)
                        </span>
                        <button
                          onClick={handleExecuteReadyToShip}
                          disabled={actionLoading}
                          style={{
                            padding: "11px 24px",
                            background: "linear-gradient(135deg, #ea580c, #c2410c)",
                            color: "#ffffff",
                            border: "none",
                            borderRadius: "8px",
                            cursor: actionLoading ? "not-allowed" : "pointer",
                            fontWeight: "600",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            boxShadow: "0 4px 15px rgba(234, 88, 12, 0.4)",
                            fontSize: "14px"
                          }}
                        >
                          {actionLoading ? "Dispatching to Shiprocket..." : selectedOrder.orderStatus === "SHIPROCKET_FAILED" ? "Retry Shiprocket Dispatch 🚀" : "Confirm & Mark Ready to Ship 🚀"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* PRODUCT INSPECTION QUICK VIEW MODAL */}
            {inspectedProduct && (
              <div className="checkout-confirm-overlay" onClick={() => setInspectedProduct(null)} style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(5px)", zIndex: 10000 }}>
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    maxWidth: "600px",
                    width: "90%",
                    background: "#141518",
                    border: "1px solid rgba(212, 175, 55, 0.4)",
                    borderRadius: "12px",
                    padding: "24px",
                    color: "#f3f4f6",
                    boxShadow: "0 15px 50px rgba(0,0,0,0.9)",
                    textAlign: "left"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "12px" }}>
                    <div>
                      <span style={{ fontSize: "12px", color: "var(--admin-gold, #d4af37)", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "600" }}>
                        Product Inspection Quick View
                      </span>
                      <h3 style={{ margin: "4px 0 0", fontFamily: "var(--font-heading)", color: "#f3e5ab", fontSize: "20px" }}>
                        #{inspectedProduct.productId} — {inspectedProduct.productName}
                      </h3>
                    </div>
                    <button onClick={() => setInspectedProduct(null)} style={{ background: "none", border: "none", fontSize: "22px", color: "#d4af37", cursor: "pointer" }}>✕</button>
                  </div>

                  <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
                    {inspectedProduct.productImages && inspectedProduct.productImages[0] && (
                      <div style={{ width: "120px", height: "160px", borderRadius: "8px", overflow: "hidden", border: "1px solid rgba(212, 175, 55, 0.3)", flexShrink: 0 }}>
                        <img
                          src={inspectedProduct.productImages[0]}
                          alt={inspectedProduct.productName}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: "220px", display: "flex", flexDirection: "column", gap: "6px", fontSize: "14px" }}>
                      <p style={{ margin: 0 }}>
                        <strong style={{ color: "#d4af37" }}>Price:</strong> ₹{inspectedProduct.productPrice}
                        {inspectedProduct.beforeDiscountPrice && (
                          <span style={{ textDecoration: "line-through", color: "#71717a", marginLeft: "8px" }}>
                            ₹{inspectedProduct.beforeDiscountPrice}
                          </span>
                        )}
                      </p>
                      <p style={{ margin: 0 }}>
                        <strong style={{ color: "#d4af37" }}>Category:</strong> {
                          (() => {
                            const catList = Array.isArray(inspectedProduct.productCategory)
                              ? inspectedProduct.productCategory
                              : inspectedProduct.productCategory
                              ? [inspectedProduct.productCategory]
                              : [];
                            if (catList.length === 0) return "N/A";
                            return (
                              catList
                                .map((c) => {
                                  const main = c?.categoryName || "";
                                  const sub = c?.subCategory?.categoryName;
                                  return sub ? `${main} / ${sub}` : main;
                                })
                                .filter(Boolean)
                                .join(", ") || "N/A"
                            );
                          })()
                        }
                      </p>
                      <p style={{ margin: 0 }}>
                        <strong style={{ color: "#d4af37" }}>Status:</strong> {inspectedProduct.status || "active"}
                      </p>
                      {inspectedProduct.orderedSizeContext && (
                        <p style={{ margin: "2px 0 0" }}>
                          <strong style={{ color: "#d4af37" }}>Customer Ordered Size:</strong>{" "}
                          <span style={{ color: "#22c55e", fontWeight: "700", background: "rgba(34, 197, 94, 0.15)", border: "1px solid rgba(34, 197, 94, 0.4)", padding: "2px 8px", borderRadius: "4px" }}>
                            {inspectedProduct.orderedSizeContext}
                          </span>
                        </p>
                      )}

                      <div style={{ marginTop: "8px" }}>
                        <strong style={{ color: "#d4af37", display: "block", marginBottom: "4px" }}>Available Sizes & Stock:</strong>
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                          {inspectedProduct.productSize?.map((sz, i) => (
                            <span
                              key={i}
                              style={{
                                background: sz.stock > 0 ? "#1e293b" : "rgba(239,68,68,0.2)",
                                color: sz.stock > 0 ? "#f3e5ab" : "#fca5a5",
                                border: sz.stock > 0 ? "1px solid rgba(212,175,55,0.3)" : "1px solid rgba(239,68,68,0.4)",
                                padding: "3px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: "600"
                              }}
                            >
                              {sz.sizeName}: {sz.stock} in stock
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Summary of how many customer orders require this product */}
                  {(() => {
                    const matchingOrders = adminOrders.filter((o) =>
                      o.orderedProducts?.some((p) => p.productId === inspectedProduct.productId)
                    );
                    const openOrders = matchingOrders.filter((o) =>
                      o.orderStatus !== "DELIVERED" && o.orderStatus !== "CANCELLED"
                    );

                    return (
                      <div style={{ background: "#1a1b20", border: "1px solid #2c2d35", borderRadius: "8px", padding: "12px", marginBottom: "16px", fontSize: "13px" }}>
                        <p style={{ margin: "0 0 6px", color: "#f3e5ab", fontWeight: "700" }}>
                          📦 Fulfillment Summary for this Product:
                        </p>
                        <p style={{ margin: "2px 0", color: "#e4e4e7" }}>
                          Found in <strong>{matchingOrders.length}</strong> total customer order(s) (<strong>{openOrders.length}</strong> open / awaiting fulfillment).
                        </p>
                      </div>
                    );
                  })()}

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                    <button
                      onClick={() => {
                        setOrderSearchQuery(String(inspectedProduct.productId));
                        setOrdersPage(1);
                        setInspectedProduct(null);
                        setSelectedOrder(null);
                      }}
                      style={{
                        padding: "8px 16px",
                        background: "var(--admin-gold, #d4af37)",
                        color: "#0b0c0e",
                        border: "none",
                        borderRadius: "6px",
                        fontWeight: "700",
                        cursor: "pointer",
                        fontSize: "13px"
                      }}
                    >
                      Filter Orders for this Product
                    </button>
                    <button
                      onClick={() => setInspectedProduct(null)}
                      style={{
                        padding: "8px 16px",
                        background: "#2c2d35",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "13px"
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ORDER CANCEL REASON MODAL */}
            {cancelModalOrder && (
              <div className="checkout-confirm-overlay" onClick={() => setCancelModalOrder(null)} style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }}>
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: "450px", width: "90%", background: "#141518", border: "1px solid rgba(212, 175, 55, 0.3)", borderRadius: "12px", padding: "24px", color: "#f3f4f6", boxShadow: "0 10px 40px rgba(0,0,0,0.8)", textAlign: "left" }}
                >
                  <h3 style={{ margin: "0 0 8px", fontFamily: "var(--font-heading)", color: "#f3e5ab", fontSize: "20px" }}>Cancel Order #{cancelModalOrder.orderId}</h3>
                  <p style={{ fontSize: "14px", color: "#a0a0aa", margin: "0 0 12px" }}>Please specify a reason for cancellation:</p>
                  <input
                    type="text"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Reason for cancellation"
                    style={{ width: "100%", padding: "12px", margin: "12px 0", background: "#1a1b20", border: "1px solid #2c2d35", borderRadius: "6px", color: "#ffffff", fontSize: "14px" }}
                  />
                  <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "16px" }}>
                    <button onClick={() => setCancelModalOrder(null)} style={{ padding: "8px 16px", background: "#374151", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}>
                      Close
                    </button>
                    <button onClick={handleExecuteCancelOrder} disabled={actionLoading} style={{ padding: "8px 16px", background: "#dc2626", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600" }}>
                      {actionLoading ? "Cancelling..." : "Confirm"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ORDER CONFIRM MODAL */}
            {confirmModalOrder && (
              <div className="checkout-confirm-overlay" onClick={() => setConfirmModalOrder(null)} style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }}>
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: "450px", width: "90%", background: "#141518", border: "1px solid rgba(212, 175, 55, 0.3)", borderRadius: "12px", padding: "24px", color: "#f3f4f6", boxShadow: "0 10px 40px rgba(0,0,0,0.8)", textAlign: "left" }}
                >
                  <h3 style={{ margin: "0 0 8px", fontFamily: "var(--font-heading)", color: "#f3e5ab", fontSize: "20px" }}>Confirm Order #{confirmModalOrder.orderId}?</h3>
                  <p style={{ fontSize: "14px", color: "#a0a0aa", margin: "0 0 16px", lineHeight: "1.5" }}>
                    Are you sure you want to mark Order #{confirmModalOrder.orderId} from <strong style={{ color: "#ffffff" }}>{confirmModalOrder.customerName || "Customer"}</strong> as Confirmed?
                  </p>
                  <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px" }}>
                    <button onClick={() => setConfirmModalOrder(null)} style={{ padding: "8px 16px", background: "#374151", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}>
                      Cancel
                    </button>
                    <button onClick={handleExecuteConfirmOrder} disabled={actionLoading} style={{ padding: "8px 16px", background: "#16a34a", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600" }}>
                      {actionLoading ? "Confirming..." : "Confirm Order"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* EDIT PACKAGE DIMENSIONS POPUP MODAL */}
            {isEditingDimensions && selectedOrder && (
              <div
                className="checkout-confirm-overlay"
                onClick={() => setIsEditingDimensions(false)}
                style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)", zIndex: 10001 }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    maxWidth: "480px",
                    width: "92%",
                    background: "#141518",
                    border: "1px solid rgba(212, 175, 55, 0.4)",
                    borderRadius: "14px",
                    padding: "24px",
                    color: "#f3f4f6",
                    boxShadow: "0 15px 50px rgba(0,0,0,0.9)",
                    textAlign: "left"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", borderBottom: "1px solid rgba(212,175,55,0.2)", paddingBottom: "12px" }}>
                    <div>
                      <span style={{ fontSize: "11px", color: "#d4af37", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "700" }}>Order Dimensions</span>
                      <h3 style={{ margin: "2px 0 0", fontFamily: "var(--font-heading)", color: "#f3e5ab", fontSize: "20px" }}>
                        Edit Dimensions — Order #{selectedOrder.orderId}
                      </h3>
                    </div>
                    <button onClick={() => setIsEditingDimensions(false)} style={{ background: "none", border: "none", fontSize: "22px", color: "#d4af37", cursor: "pointer" }}>✕</button>
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#d4af37", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                      Total Package Weight (kg) *
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type="number"
                        step="0.01"
                        min="0.05"
                        max="50"
                        required
                        value={shipWeight}
                        onChange={(e) => setShipWeight(e.target.value)}
                        placeholder="e.g. 0.50"
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          background: "#1f2026",
                          border: "1px solid rgba(212,175,55,0.3)",
                          borderRadius: "8px",
                          color: "#ffffff",
                          fontSize: "15px",
                          fontWeight: "600"
                        }}
                      />
                      <span style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#888", fontSize: "12px" }}>KG</span>
                    </div>
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#d4af37", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                      Package Dimensions (cm)
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
                      <div>
                        <span style={{ fontSize: "11px", color: "#a0a0aa", display: "block", marginBottom: "2px" }}>Length (L)</span>
                        <input
                          type="number"
                          step="0.5"
                          min="1"
                          required
                          value={shipLength}
                          onChange={(e) => setShipLength(e.target.value)}
                          style={{ width: "100%", padding: "8px 10px", background: "#1f2026", border: "1px solid #333", borderRadius: "6px", color: "#fff", fontSize: "14px" }}
                        />
                      </div>
                      <div>
                        <span style={{ fontSize: "11px", color: "#a0a0aa", display: "block", marginBottom: "2px" }}>Width (W)</span>
                        <input
                          type="number"
                          step="0.5"
                          min="1"
                          required
                          value={shipWidth}
                          onChange={(e) => setShipWidth(e.target.value)}
                          style={{ width: "100%", padding: "8px 10px", background: "#1f2026", border: "1px solid #333", borderRadius: "6px", color: "#fff", fontSize: "14px" }}
                        />
                      </div>
                      <div>
                        <span style={{ fontSize: "11px", color: "#a0a0aa", display: "block", marginBottom: "2px" }}>Height (H)</span>
                        <input
                          type="number"
                          step="0.5"
                          min="1"
                          required
                          value={shipHeight}
                          onChange={(e) => setShipHeight(e.target.value)}
                          style={{ width: "100%", padding: "8px 10px", background: "#1f2026", border: "1px solid #333", borderRadius: "6px", color: "#fff", fontSize: "14px" }}
                        />
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#d4af37", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                      Pickup Location Name
                    </label>
                    <input
                      type="text"
                      value={shipPickupLocation}
                      onChange={(e) => setShipPickupLocation(e.target.value)}
                      placeholder="Primary"
                      style={{ width: "100%", padding: "10px 12px", background: "#1f2026", border: "1px solid #333", borderRadius: "6px", color: "#fff", fontSize: "14px" }}
                    />
                  </div>

                  <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      onClick={() => setIsEditingDimensions(false)}
                      style={{ padding: "8px 16px", background: "#27272a", color: "#e4e4e7", border: "1px solid #3f3f46", borderRadius: "6px", cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingDimensions(false);
                        setSubmitMessage({ type: "success", text: "Package dimensions updated!" });
                      }}
                      style={{
                        padding: "8px 18px",
                        background: "#16a34a",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: "600"
                      }}
                    >
                      Save Dimensions ✓
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* MODE 5: INVENTORY & RETAIL POS */}
            {adminMode === "inventory" && (
              <AdminInventory
                api={api}
                categories={categories}
                addNotification={addNotification}
              />
            )}
          </main>
        </div>
      </div>
  );
}

function AdminAminii() {
  const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
  return (
    <NotificationProvider authToken={token}>
      <AdminAminiiContent />
    </NotificationProvider>
  );
}

export default AdminAminii;
